import React, { useEffect, useState, useMemo } from 'react'
import Select from 'react-select'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'
import { selectDropdowns, selectDropdownOptions } from '../../../redux/dropdownSlice'
import { useRequest } from '../../../hooks/UseRequest'
import { useDispatch, useSelector } from 'react-redux'
import { setNotification } from '../../../redux/notificationSlice'
import { useForm, Controller } from 'react-hook-form'
import { selectAuth } from "../../../redux/authSlice"
import DeclineIcon from '../../../components/elements/DeclineIcon'
import SuccessIcon from '../../../components/elements/SuccessIcon'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQuestionCircle, faRefresh, faArrowUpRightFromSquare, faUndo, faSpinner } from '@fortawesome/free-solid-svg-icons'
import AsyncSelect from 'react-select/async'
import ViewSensitivePiiButtons from "../../../components/ViewSensitivePiiButtons";
import { CurrencyFormat, PercentFormat, FormatISODateTime, formatApr } from '../../../helpers/formatting'
import ManagerApprovalIcons from '../../../components/elements/ManagerApprovalIcons'
import { setModal, selectModal } from '../../../redux/modalSlice'
import { triggerRerender } from '../../../redux/rerenderSlice'

/**
 * Step 1 of verifications process. Allows the Agent to verify that all the customer date is correct.
 * @constructor
 */
export default function VerificationQuestions(props) {
  const {
    setStep,
    verificationFields,
    latestVerificationBankAccountId,
    startVerification,
    verificationFieldsObject,
    verificationId,
    setVerificationId,
    customerData,
    updateVerificationFields,
    setPtiTerms,
    setPaymentScheduleData,
    setPaymentScheduleDetails,
    currentPtiTerms,
    managerApproval,
    setManagerApprovalPoll,
    getManagerApprovalStatus,
    bankChangeApproval,
    setBankChangeApprovalPoll,
    getBankChangeApprovalStatus,
    setIsPrequalified,
    setPrequalifiedData,
    processVerificationQuestions,
    isReadOnly,
    getNextPaymentDates,
    nextPaymentDatesTooltip,
  } = props
  const requests = useRequest()
  const dispatch = useDispatch()
  const dropdowns = useSelector(selectDropdowns)
  const dropdownOptions = useSelector(selectDropdownOptions)
  const auth = useSelector(selectAuth)
  const modal = useSelector(selectModal);

  const [isPayrollConfirmed, setIsPayrollConfirmed] = useState(false);

  const requiredCodeKeysForSubmission = ['income_type_employed'];
  const requiredIdsForSubmission = dropdownOptions
    .filter((o) => requiredCodeKeysForSubmission.includes(o.code_key))
    .map((o) => o.id);

  const [verificationFieldsObjectState, setVerificationFieldsObjectState] = useState(verificationFieldsObject);

  const sourceOfIncome = dropdowns.data.filter((o) => o.code_key === 'income_type')[0]
  const sourceOfIncomeOptions = dropdownOptions.filter((o) => o.dropdown_id === sourceOfIncome.id).map((o) => ({ label: o.name, value: o.id, codeKey: o.code_key }))

  const payFrequency = dropdowns.data.filter((o) => o.code_key === 'pay_frequency')[0]
  const payFrequencyOptions = dropdownOptions.filter((o) => o.dropdown_id === payFrequency.id && o.code_key !== 'pay_frequency_daily').map((o) => ({ label: o.name, value: o.id }))

  const dayOfWeek = dropdowns.data.filter((o) => o.code_key === 'day_of_week')[0]
  const dayOfWeekOptions = dropdownOptions.filter((o) => o.dropdown_id === dayOfWeek.id).map((o) => ({ label: o.name, value: o.id }))

  const weekOfMonth = dropdowns.data.filter((o) => o.code_key === 'week_of_month')[0]
  const weekOfMonthOptions = dropdownOptions.filter((o) => o.dropdown_id === weekOfMonth.id).map((o) => ({ label: o.name, value: o.id }))

  const dayOfMonth = dropdowns.data.filter((o) => o.code_key === 'day_of_month')[0]
  const dayOfMonthOptions = dropdownOptions.filter((o) => o.dropdown_id === dayOfMonth.id).map((o) => ({ label: o.name, value: o.id }))

  const processingStatusCompletedId = dropdownOptions.find((o) => o?.code_key === 'processing_status_completed')?.id
  const processingStatusPendingId = dropdownOptions.find((o) => o?.code_key === 'processing_status_pending')?.id

  const [payFrequencyValue, setPayFrequencyValue] = useState("")
  const [payFrequencyValueOption, setPayFrequencyValueOption] = useState("")
  const [saving, setSaving] = useState(false)
  const [btnUndoVerify, setBtnUndoVerify] = useState(false)
  const [bankLookupLoading, setBankLookupLoading] = useState(false)
  const [incomeTypeSelection, setIncomeTypeSelection] = useState(null)
  const [disableNextButton, setDisableNextButton] = useState(false)

  let todayDate = new Date();
  const today = todayDate.toISOString().split("T")[0];
  // Add 14 days to the current date
  const future = new Date();
  future.setDate(todayDate.getDate() + 14);
  // Format the future date as YYYY-MM-DD using toISOString
  const maxDate = future.toISOString().split("T")[0];

  // Holds all selected banks including current to decrease BE hits for this page
  const [selectedBanks, setSelectedBanks] = useState({})
  // The current selected bank, used to show bank name in places other than the dropdown
  const [currentBank, setCurrentBank] = useState({})

  const registerForm = () => {
    const { register, handleSubmit, setError, control, setValue, watch } = useForm()
    return { register, handleSubmit, setError, control, setValue, watch }
  }

  const forms = {
    verification_field_first_name: registerForm(),
    verification_field_last_name: registerForm(),
    verification_field_ssn_last_four: registerForm(),
    verification_field_birth_date: registerForm(),
    verification_field_home_phone_number: registerForm(),
    verification_field_cell_phone_number: registerForm(),
    verification_field_email_address: registerForm(),
    verification_field_bank_account_number: registerForm(),
    verification_field_income_type_dropdown_option_id: registerForm(),
    verification_field_employer_name: registerForm(),
    verification_field_employer_phone: registerForm(),
    verification_field_job_title: registerForm(),
    verification_field_net_monthly_income: registerForm(),
    verification_field_pay_frequency_dropdown_option_id: registerForm(),
    semi_monthly_pay_frequency: registerForm(),
    monthly_pay_frequency: registerForm(),
    verification_field_is_pay_before_holiday: registerForm(),
    verification_field_next_pay_date: registerForm(), // biweekly
    verification_field_pay_day_of_week: registerForm(),
    verification_field_pay_first_day_of_week_dropdown_option_id: registerForm(), // weekly
    verification_field_pay_second_day_of_week_dropdown_option_id: registerForm(), // weekly
    verification_field_pay_first_day_of_month_dropdown_option_id: registerForm(), // semi-monthly 1.1
    verification_field_pay_second_day_of_month_dropdown_option_id: registerForm(), // semi-monthly 1.2
    verification_field_pay_first_week_of_month_dropdown_option_id: registerForm(), // semi-monthly 2.1
    verification_field_pay_second_week_of_month_dropdown_option_id: registerForm(), // semi-monthly 2.2
    verification_field_is_automatic_payments: registerForm(),
    verification_field_is_confirm_pay_dates: registerForm(),
  }

  // Watch for changes in the selected income type from the form
  const watchSourceOfIncome = forms.verification_field_income_type_dropdown_option_id.watch('verification_field_income_type_dropdown_option_id');

  // Get the dropdown option ID for Social Security and Disability Income types
  const socialSecurityDropdownOptionId = dropdownOptions.find((item) => item?.code_key === 'income_type_social_security')?.id;
  const disabilityIncomeDropdownOptionId = dropdownOptions.find((item) => item?.code_key === 'income_type_disability_income')?.id;

  // Get the dropdown option object for Monthly pay frequency
  const payFrequencyMonthlyDropdownOption = dropdownOptions
    .filter((item) => item?.code_key === 'pay_frequency_monthly')
    .map((o) => ({ label: o.name, value: o.id }))[0];

  // Auto-set pay frequency to Monthly when income type is Social Security or Disability Income
  useEffect(() => {
    if (watchSourceOfIncome?.value === socialSecurityDropdownOptionId || watchSourceOfIncome?.value === disabilityIncomeDropdownOptionId) {
      let processingStatusDropdownOptionId = verificationFields['verification_field_pay_frequency_dropdown_option_id']?.processing_status_dropdown_option_id

      // if pay frequecy is unverified then set it to monthly
      if (processingStatusDropdownOptionId != processingStatusCompletedId) {
        forms.verification_field_pay_frequency_dropdown_option_id.setValue('verification_field_pay_frequency_dropdown_option_id', payFrequencyMonthlyDropdownOption);
        setPayFrequencyValue(payFrequencyMonthlyDropdownOption?.label)
      }
    }
  }, [watchSourceOfIncome]);

  const markAsUnverified = (verification_field) => {
    // TODO in LOX-2137: have to display old values in the field
    let prevState = verificationFields[verification_field]
    prevState.processing_status_dropdown_option_id = processingStatusPendingId
    updateVerificationFields(verification_field, prevState)
    renderStatusActions(verification_field)
  }

  const handleEditAndCloseClick = (verification_field, value, isCanceled = false) => {
    setVerificationFieldsObjectState((prevState) => ({
      ...prevState,
      [verification_field]: {
        ...prevState[verification_field],
        isEditing: value,
      }
    }));

    if (isCanceled) {
      // when clicking the cancel button reset the verification field to its original value, even after the user has made changes
      const defaultValue = parseDefaultValue(verification_field);
      if (verification_field == 'verification_field_bank_account_number') {
        forms.verification_field_bank_account_number.setValue('verification_field_is_bank_savings', [{ label: 'Savings', value: 1 }, { label: 'Checking', value: 0 }].find((option) => option.value == parseDefaultValue('verification_field_is_bank_savings')) ?? '')
        forms.verification_field_bank_account_number.setValue('verification_field_is_online_banking', [{ label: 'Yes', value: 1 }, { label: 'No', value: 0 }].find((option) => option.value == parseDefaultValue('verification_field_is_online_banking')) ?? '')
        forms.verification_field_bank_account_number.setValue('verification_field_is_direct_deposit', [{ label: 'Yes', value: 1 }, { label: 'No', value: 0 }].find((option) => option.value == parseDefaultValue('verification_field_is_direct_deposit')) ?? '')
        forms.verification_field_bank_account_number.setValue('verification_field_bank_routing_number', parseDefaultValue('verification_field_bank_routing_number'));
        forms[verification_field].setValue(verification_field, defaultValue);
      } else if (verification_field == 'verification_field_income_type_dropdown_option_id') {
        forms.verification_field_income_type_dropdown_option_id.setValue('verification_field_income_type_dropdown_option_id', sourceOfIncomeOptions.find((option) => option.value == defaultValue) ?? '')
      } else if (verification_field == 'verification_field_pay_frequency_dropdown_option_id') {
        const val = payFrequencyOptions.find((option) => option.value == defaultValue)
        forms.verification_field_pay_frequency_dropdown_option_id.setValue('verification_field_pay_frequency_dropdown_option_id', val ?? '')
        setPayFrequencyValue(val?.label)
      } else if (verification_field == 'verification_field_is_pay_before_holiday') {
        forms.verification_field_is_pay_before_holiday.setValue('verification_field_is_pay_before_holiday', [{ label: 'Before', value: 1 }, { label: 'After', value: 0 }].find((option) => option.value == defaultValue) ?? '')
      } else if (verification_field == 'verification_field_is_automatic_payments') {
        forms.verification_field_is_automatic_payments.setValue('verification_field_is_automatic_payments', [{ label: 'Yes', value: 1 }, { label: 'No', value: 0 }].find((option) => option.value == defaultValue) ?? '')
      } else if (verification_field == 'verification_field_is_confirm_pay_dates') {
        forms.verification_field_is_confirm_pay_dates.setValue('verification_field_is_confirm_pay_dates', [{ label: 'Yes', value: 1 }, { label: 'No', value: 0 }].find((option) => option.value == defaultValue) ?? '')
      } else {
        forms[verification_field].setValue(verification_field, defaultValue);
      }
    }
  };

  const handleLoading = (verification_field, value) => {
    setVerificationFieldsObjectState((prevState) => ({
      ...prevState,
      [verification_field]: {
        ...prevState[verification_field],
        isLoading: value,
      }
    }));
  };

  const confirmBeforeVerifyPayroll = () => {
    dispatch(setModal({
      is_active: true,
      extraOptions: {
        is_payroll_confirmation: true,
        type: 'payroll_amount_confirmation_modal',
        callMethod: () => {
          setIsPayrollConfirmed(true);

          // Delay form re-submission to ensure the state update above is reflected.
          // We manually trigger the form's submit event, which causes react-hook-form's handleSubmit(onSubmit) to re-execute with the latest form state.
          setTimeout(() => {
            document.getElementById('net_monthly_income_form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
          }, 0);
        },
        closeMethod: () => setIsPayrollConfirmed(false)
      }
    }));
  };

  const onSubmit = async (formData) => {
    const verificationFieldCodeKey = Object.keys(formData)[0]
    const fieldValue = formData[verificationFieldCodeKey]?.value ?? formData[verificationFieldCodeKey]
    if (verificationFieldCodeKey === 'verification_field_net_monthly_income') {
      const amount = parseFloat(fieldValue);
      if ((amount < 100 || amount > 10000) && !isPayrollConfirmed) {
        confirmBeforeVerifyPayroll();
        return;
      }
      setIsPayrollConfirmed(false);
    }

    const sanitizedData = {
      field_value: fieldValue,
      verification_id: verificationId,
      verification_field_dropdown_option_id: dropdownOptions.filter((item) => item.code_key === verificationFieldCodeKey)[0].id,
      processing_status_dropdown_option_id: processingStatusCompletedId,
    };
    requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/manual_verification`)
    handleLoading(verificationFieldCodeKey, true)
    setDisableNextButton(true)
    try {
      const response = btnUndoVerify ? await requests.modify(sanitizedData) : await requests.post(sanitizedData)
      if (response.status === 200 || response.status === 201) {
        setDisableNextButton(false)
        if (btnUndoVerify) {
          markAsUnverified(verificationFieldCodeKey)
        } else {
          response?.data?.verification_fields.forEach((field) => {
            const fieldCodeKey = dropdownOptions.find((x) => x?.id === field.verification_field_dropdown_option_id)?.code_key
            updateVerificationFields(fieldCodeKey, field)
            handleEditAndCloseClick(fieldCodeKey, false)
          })
        }

        // If the verification field is pay before holiday, we need to get the next payment dates
        if (verificationFieldCodeKey === 'verification_field_is_pay_before_holiday') {
          getNextPaymentDates()
        }

        if (response?.data?.error_message) {
          dispatch(setNotification({ type: 'error', body: response.data.error_message }))
        } else {
          dispatch(setNotification({ type: 'success', body: '' }))
        }
      } else {
        setDisableNextButton(false)
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      setDisableNextButton(false)
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
    } finally {
      handleLoading(verificationFieldCodeKey, false)
      setBtnUndoVerify(false)
    }
  };
  function isNumeric(str) {
    return /^\d+$/.test(str);
  }

  /**
   * submit form for
   */
  const onBankAccountVerificationSubmit = async (formData) => {
    // Handle masked account numbers for undo
    const accountNumber = formData.verification_field_bank_account_number?.trim();
    const isMaskedAccountNumber = accountNumber && accountNumber.includes('#');
    // If this is a submit operation (not undo) and account number contains '#', prevent saving
    if (!btnUndoVerify && isMaskedAccountNumber) {
      dispatch(setNotification({ type: 'error', body: "Account number can only contain digits." }));
      return false;
    }
    // Validate required fields are not empty
    if (!btnUndoVerify) {
      if (!accountNumber || accountNumber === '') {
        dispatch(setNotification({ type: 'error', body: "Account number is required." }));
        return false;
      }
      if (accountNumber.length > 17) {
        dispatch(setNotification({ type: 'error', body: "Account number cannot exceed 17 digits." }));
        return false;
      }
      if (!formData.verification_field_bank_routing_number?.trim()) {
        dispatch(setNotification({ type: 'error', body: "Routing number is required." }));
        return false;
      }
      if (formData.verification_field_bank_routing_number?.length !== 9) {
        dispatch(setNotification({ type: 'error', body: "Routing number must be 9 digits." }));
        return false;
      }
      if (!formData.verification_field_bank_name?.value) {
        dispatch(setNotification({ type: 'error', body: "Bank name is required." }));
        return false;
      }
      if (!formData.verification_field_is_bank_savings === undefined) {
        dispatch(setNotification({ type: 'error', body: "Bank account type is required." }));
        return false;
      }
      if (!formData.verification_field_is_online_banking === undefined) {
        dispatch(setNotification({ type: 'error', body: "Online banking selection is required." }));
        return false;
      }
      if (!formData.verification_field_is_direct_deposit === undefined) {
        dispatch(setNotification({ type: 'error', body: "Direct deposit selection is required." }));
        return false;
      }
    }

    // Validate field formats
    if (accountNumber && !isMaskedAccountNumber && !isNumeric(accountNumber)) {
      dispatch(setNotification({ type: 'error', body: "Account number can only contain digits." }));
      return false;
    }
    if (formData.verification_field_bank_routing_number?.trim() && !isNumeric(formData.verification_field_bank_routing_number.trim())) {
      dispatch(setNotification({ type: 'error', body: "Routing number can only contain digits." }));
      return false;
    }

    handleLoading("verification_field_bank_account_number", true);
    requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/manual_verification`);
    setDisableNextButton(true);

    const fieldsData = [
      {
        field_value: formData.verification_field_bank_account_number?.trim(),
        verification_field_dropdown_option_id: dropdownOptions.filter((item) => item.code_key === 'verification_field_bank_account_number')[0].id,
      },
      {
        field_value: formData.verification_field_bank_routing_number?.trim(),
        verification_field_dropdown_option_id: dropdownOptions.filter((item) => item.code_key === 'verification_field_bank_routing_number')[0].id,
      },
      {
        field_value: formData.verification_field_is_bank_savings.value,
        verification_field_dropdown_option_id: dropdownOptions.filter((item) => item.code_key === 'verification_field_is_bank_savings')[0].id,
      },
      {
        field_value: formData.verification_field_is_online_banking.value,
        verification_field_dropdown_option_id: dropdownOptions.filter((item) => item.code_key === 'verification_field_is_online_banking')[0].id,
      },
      {
        field_value: formData.verification_field_is_direct_deposit.value,
        verification_field_dropdown_option_id: dropdownOptions.filter((item) => item.code_key === 'verification_field_is_direct_deposit')[0].id,
      },
      {
        field_value: formData.verification_field_bank_name.value,
        verification_field_dropdown_option_id: dropdownOptions.filter((item) => item.code_key === 'verification_field_bank_name')[0].id,
      },
    ];

    try {
      const payload = {
        verification_id: verificationId,
        verification_request: dropdownOptions.filter((item) => item.code_key === 'verification_field_bank_account_number')[0].id,
        processing_status_dropdown_option_id: processingStatusCompletedId,
        fields: fieldsData
      };

      const response = btnUndoVerify ? await requests.modify(payload) : await requests.post(payload);
      if (response.status === 200 || response.status === 201) {
        setDisableNextButton(false)
        if (btnUndoVerify) {
          markAsUnverified('verification_field_bank_account_number')
        } else if (response?.data?.error_message) {
          const accountNumberVerification = response?.data?.verification_fields.find(
            (field) => field.verification_field_dropdown_option_id === dropdownOptions.filter((item) => item.code_key === 'verification_field_bank_account_number')[0].id
          );
          if (accountNumberVerification?.processing_status_dropdown_option_id === dropdownOptions.filter((item) => item.code_key === 'processing_status_verify')[0].id) {
            await getManagerApprovalStatus();
            setManagerApprovalPoll(true);
          }
          if (accountNumberVerification?.processing_status_dropdown_option_id === dropdownOptions.filter((item) => item.code_key === 'bank_change_outcome_duplicate_pending')[0].id) {
            await getBankChangeApprovalStatus();
            setBankChangeApprovalPoll(true);
          }
          dispatch(setNotification({ type: 'warning', body: response.data.error_message }));
        } else {
          await getManagerApprovalStatus();
          dispatch(setNotification({ type: 'success', body: '' }));
        }
        dispatch(triggerRerender({ subpanelId: 'bank_change_dashlet' }));
        if (!btnUndoVerify) {
          response?.data?.verification_fields.forEach((field) => {
            const fieldCodeKey = dropdownOptions.find((x) => x?.id === field.verification_field_dropdown_option_id)?.code_key
            updateVerificationFields(fieldCodeKey, field)
            handleEditAndCloseClick(fieldCodeKey, false)
          });
        }
      } else {
        setDisableNextButton(false)
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }));
      }
    } catch (error) {
      setDisableNextButton(false)
      if (typeof error.response?.data?.errors != 'undefined' && Object.keys(error.response?.data?.errors).length > 0) {
        const body = parseErrorResponse(error.response?.data?.errors, fieldsData)
        dispatch(setNotification({ type: 'error', body: body }))
      } else {
        dispatch(setNotification({ type: 'error', body: error.response?.data?.message }));
      }
    } finally {
      handleLoading("verification_field_bank_account_number", false);
      setBtnUndoVerify(false)
    }
  };

  /**
 * submit form for pay frequency dropdown option id and its related fields (weekly, biweekly, semi-monthly, monthly)
 */
  const onPayFrequencySubmit = async (formData) => {
    let data = {
      verification_field_pay_frequency_dropdown_option_id: formData.verification_field_pay_frequency_dropdown_option_id.value,
      verification_field_pay_first_day_of_week_dropdown_option_id: "",
      verification_field_next_pay_date: "",
      verification_field_pay_first_day_of_month_dropdown_option_id: "",
      verification_field_pay_second_day_of_month_dropdown_option_id: "",
      verification_field_pay_first_week_of_month_dropdown_option_id: "",
      verification_field_pay_second_week_of_month_dropdown_option_id: "",
      verification_field_pay_second_day_of_week_dropdown_option_id: "",
    }

    if (formData.verification_field_pay_frequency_dropdown_option_id.value === dropdownOptions.find((option) => option?.code_key === 'pay_frequency_weekly')?.id) {
      if (!formData.verification_field_pay_first_day_of_week_dropdown_option_id.value) {
        dispatch(setNotification({ type: 'error', body: "Pay day of week is required" }))
        return;
      } else {
        data.verification_field_pay_first_day_of_week_dropdown_option_id = formData.verification_field_pay_first_day_of_week_dropdown_option_id.value
      }
    }

    if (formData.verification_field_pay_frequency_dropdown_option_id.value === dropdownOptions.find((option) => option?.code_key === 'pay_frequency_biweekly')?.id) {
      if (!formData.verification_field_next_pay_date) {
        dispatch(setNotification({ type: 'error', body: "Next Pay Date is required" }))
        return;
      } else {
        data.verification_field_next_pay_date = formData.verification_field_next_pay_date
      }
    }

    if (formData.verification_field_pay_frequency_dropdown_option_id.value === dropdownOptions.find((option) => option?.code_key === 'pay_frequency_semimonthly')?.id) {
      if (payFrequencyValueOption === "semimonthly-option1") {
        if (!formData.verification_field_pay_first_day_of_month_dropdown_option_id.value) {
          dispatch(setNotification({ type: 'error', body: "First payment day of month is required" }))
          return;
        } else if (!formData.verification_field_pay_second_day_of_month_dropdown_option_id.value) {
          dispatch(setNotification({ type: 'error', body: "Second payment day of month is required" }))
          return;
        } else {
          data.verification_field_pay_first_day_of_month_dropdown_option_id = formData.verification_field_pay_first_day_of_month_dropdown_option_id.value
          data.verification_field_pay_second_day_of_month_dropdown_option_id = formData.verification_field_pay_second_day_of_month_dropdown_option_id.value
        }
      } else if (payFrequencyValueOption === "semimonthly-option2") {
        if (!formData.verification_field_pay_first_week_of_month_dropdown_option_id.value) {
          dispatch(setNotification({ type: 'error', body: "Week 1 is required" }))
          return;
        } else if (!formData.verification_field_pay_first_day_of_week_dropdown_option_id.value) {
          dispatch(setNotification({ type: 'error', body: "Day 1 is required" }))
          return;
        } else if (!formData.verification_field_pay_second_week_of_month_dropdown_option_id.value) {
          dispatch(setNotification({ type: 'error', body: "Week 2 is required" }))
          return;
        } else if (!formData.verification_field_pay_second_day_of_week_dropdown_option_id.value) {
          dispatch(setNotification({ type: 'error', body: "Day 2 is required" }))
          return;
        } else {
          data.verification_field_pay_first_week_of_month_dropdown_option_id = formData.verification_field_pay_first_week_of_month_dropdown_option_id.value
          data.verification_field_pay_first_day_of_week_dropdown_option_id = formData.verification_field_pay_first_day_of_week_dropdown_option_id.value
          data.verification_field_pay_second_week_of_month_dropdown_option_id = formData.verification_field_pay_second_week_of_month_dropdown_option_id.value
          data.verification_field_pay_second_day_of_week_dropdown_option_id = formData.verification_field_pay_second_day_of_week_dropdown_option_id.value
        }
      } else {
        dispatch(setNotification({ type: 'error', body: "Please select a semi monthly option" }));
        return;
      }
    }

    if (formData.verification_field_pay_frequency_dropdown_option_id.value === dropdownOptions.find((option) => option?.code_key === 'pay_frequency_monthly')?.id) {
      if (payFrequencyValueOption === "monthly-option1") {
        if (!formData.verification_field_pay_first_day_of_month_dropdown_option_id.value) {
          dispatch(setNotification({ type: 'error', body: "Day is required" }))
          return;
        } else {
          data.verification_field_pay_first_day_of_month_dropdown_option_id = formData.verification_field_pay_first_day_of_month_dropdown_option_id.value
        }
      } else if (payFrequencyValueOption === "monthly-option2") {
        if (!formData.verification_field_pay_first_week_of_month_dropdown_option_id.value) {
          dispatch(setNotification({ type: 'error', body: "Week is required" }))
          return;
        } else if (!formData.verification_field_pay_first_day_of_week_dropdown_option_id.value) {
          dispatch(setNotification({ type: 'error', body: "Day is required" }))
          return;
        } else {
          data.verification_field_pay_first_week_of_month_dropdown_option_id = formData.verification_field_pay_first_week_of_month_dropdown_option_id.value
          data.verification_field_pay_first_day_of_week_dropdown_option_id = formData.verification_field_pay_first_day_of_week_dropdown_option_id.value
        }
      } else {
        dispatch(setNotification({ type: 'error', body: "Please select a monthly option" }));
        return;
      }
    }

    const fieldsData = [
      {
        field_value: data.verification_field_pay_frequency_dropdown_option_id,
        verification_field_dropdown_option_id: dropdownOptions.filter((item) => item.code_key === 'verification_field_pay_frequency_dropdown_option_id')[0].id,
      },
      {
        field_value: data.verification_field_pay_first_day_of_week_dropdown_option_id,
        verification_field_dropdown_option_id: dropdownOptions.filter((item) => item.code_key === 'verification_field_pay_first_day_of_week_dropdown_option_id')[0].id,
      },
      {
        field_value: data.verification_field_next_pay_date,
        verification_field_dropdown_option_id: dropdownOptions.filter((item) => item.code_key === 'verification_field_next_pay_date')[0].id,
      },
      {
        field_value: data.verification_field_pay_first_day_of_month_dropdown_option_id,
        verification_field_dropdown_option_id: dropdownOptions.filter((item) => item.code_key === 'verification_field_pay_first_day_of_month_dropdown_option_id')[0].id,
      },
      {
        field_value: data.verification_field_pay_second_day_of_month_dropdown_option_id,
        verification_field_dropdown_option_id: dropdownOptions.filter((item) => item.code_key === 'verification_field_pay_second_day_of_month_dropdown_option_id')[0].id,
      },
      {
        field_value: data.verification_field_pay_first_week_of_month_dropdown_option_id,
        verification_field_dropdown_option_id: dropdownOptions.filter((item) => item.code_key === 'verification_field_pay_first_week_of_month_dropdown_option_id')[0].id,
      },
      {
        field_value: data.verification_field_pay_second_week_of_month_dropdown_option_id,
        verification_field_dropdown_option_id: dropdownOptions.filter((item) => item.code_key === 'verification_field_pay_second_week_of_month_dropdown_option_id')[0].id,
      },
      {
        field_value: data.verification_field_pay_second_day_of_week_dropdown_option_id,
        verification_field_dropdown_option_id: dropdownOptions.filter((item) => item.code_key === 'verification_field_pay_second_day_of_week_dropdown_option_id')[0].id,
      }
    ];

    const payload = {
      verification_id: verificationId,
      processing_status_dropdown_option_id: processingStatusCompletedId,
      fields: fieldsData
    };

    handleLoading("verification_field_pay_frequency_dropdown_option_id", true);
    requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/manual_verification`);
    setDisableNextButton(true)
    try {
      const response = btnUndoVerify ? await requests.modify(payload) : await requests.post(payload)
      if (response.status !== 200 && response.status !== 201) {
        setDisableNextButton(false)
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }));
      }
      if (btnUndoVerify) {
        setDisableNextButton(false)
        markAsUnverified('verification_field_pay_frequency_dropdown_option_id')
      } else {
        setDisableNextButton(false)
        response?.data?.verification_fields.forEach((field) => {
          const fieldCodeKey = dropdownOptions.find((x) => x?.id === field.verification_field_dropdown_option_id)?.code_key
          updateVerificationFields(fieldCodeKey, field)
          handleEditAndCloseClick(fieldCodeKey, false)
        })
      }

      // If the verification field is pay frequency, we need to get the next payment dates
      getNextPaymentDates()
    } catch (error) {
      setDisableNextButton(false)
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }));
    } finally {
      handleLoading("verification_field_pay_frequency_dropdown_option_id", false);
      setBtnUndoVerify(false)
    }
  };

  /**
  * submit form for source of income together with its related fields
  */
  const onSourceOfIncomeSubmit = async (formData) => {
    let data = {
      verification_field_income_type_dropdown_option_id: formData.verification_field_income_type_dropdown_option_id.value,
      verification_field_employer_name: formData.verification_field_employer_name,
      verification_field_employer_phone: formData.verification_field_employer_phone,
      verification_field_job_title: formData.verification_field_job_title,
    }

    if (Object.prototype.hasOwnProperty.call(formData, 'verification_field_employer_name') &&
      (formData.verification_field_employer_name === "" || formData.verification_field_employer_name == null) &&
      requiredIdsForSubmission.includes(+formData?.verification_field_income_type_dropdown_option_id?.value)) {
      dispatch(setNotification({ type: 'error', body: "Current employer is required" }));
      return;
    } else if (Object.prototype.hasOwnProperty.call(formData, 'verification_field_employer_phone') &&
      (formData.verification_field_employer_phone === "" || formData.verification_field_employer_phone == null) &&
      requiredIdsForSubmission.includes(+formData?.verification_field_income_type_dropdown_option_id?.value)) {
      dispatch(setNotification({ type: 'error', body: "Employer Phone is required" }));
      return;
    } else if (Object.prototype.hasOwnProperty.call(formData, 'verification_field_job_title') &&
      (formData.verification_field_job_title === "" || formData.verification_field_job_title == null) &&
      requiredIdsForSubmission.includes(+formData?.verification_field_income_type_dropdown_option_id?.value)) {
      dispatch(setNotification({ type: 'error', body: "Position is required" }));
      return;
    }

    const fieldsData = [
      {
        field_value: data.verification_field_income_type_dropdown_option_id,
        verification_field_dropdown_option_id: dropdownOptions.filter((item) => item.code_key === 'verification_field_income_type_dropdown_option_id')[0].id,
      },
      {
        field_value: data.verification_field_employer_name,
        verification_field_dropdown_option_id: dropdownOptions.filter((item) => item.code_key === 'verification_field_employer_name')[0].id,
      },
      {
        field_value: data.verification_field_employer_phone,
        verification_field_dropdown_option_id: dropdownOptions.filter((item) => item.code_key === 'verification_field_employer_phone')[0].id,
      },
      {
        field_value: data.verification_field_job_title,
        verification_field_dropdown_option_id: dropdownOptions.filter((item) => item.code_key === 'verification_field_job_title')[0].id,
      },
    ];

    const payload = {
      verification_id: verificationId,
      processing_status_dropdown_option_id: processingStatusCompletedId,
      fields: fieldsData
    };

    handleLoading("verification_field_income_type_dropdown_option_id", true);
    requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/manual_verification`);
    setDisableNextButton(true)
    try {
      const response = btnUndoVerify ? await requests.modify(payload) : await requests.post(payload)
      if (response.status !== 200 && response.status !== 201) {
        setDisableNextButton(false)
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }));
      }
      if (btnUndoVerify) {
        setDisableNextButton(false)
        markAsUnverified('verification_field_income_type_dropdown_option_id')
      } else {
        setDisableNextButton(false)
        response?.data?.verification_fields.forEach((field) => {
          const fieldCodeKey = dropdownOptions.find((x) => x?.id === field.verification_field_dropdown_option_id)?.code_key
          updateVerificationFields(fieldCodeKey, field)
          handleEditAndCloseClick(fieldCodeKey, false)
        })
      }
    } catch (error) {
      setDisableNextButton(false)
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }));
    } finally {
      handleLoading("verification_field_income_type_dropdown_option_id", false);
      setBtnUndoVerify(false)
    }
  };

  let typingTimeout = null
  const getBanks = async (inputValue, column, operator) => {
    return new Promise((resolve) => {
      if (typingTimeout) clearTimeout(typingTimeout);

      typingTimeout = setTimeout(async () => {
        const filterQuery = {
          and: [
            { column: column, value: inputValue, operator: operator },
          ],
        };

        requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/bank?filters=${JSON.stringify(filterQuery)}`);
        const response = await requests.get().then((response) => response.data.data);

        resolve(response);
      }, 1000);
    });
  };

  /**
   * Once the bank routing number or bank name field is set we use this function to get the value to populate the
   * bank name dropdown. This is primarily called during the useEffect to set default values, and includes code to
   * protect against having to call the BE on every useEffect re-render.
   * @param bankId
   * @returns {Promise<void>}
   */
  const setBank = async (bankId) => {
    // Check to ensure the bank id is numeric
    const regex = /^\d+$/;
    if (regex.test(bankId)) {
      // Check if we already have pulled this bank record
      if (selectedBanks[bankId]) {
        forms.verification_field_bank_account_number.setValue('verification_field_bank_name', selectedBanks[bankId])
        setCurrentBank(selectedBanks[bankId])
        return;
      }

      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/bank/${bankId}`);
      const response = await requests.get()
      if (response?.data?.data) {
        const newBankId = response.data.data.id
        const selectData = { label: response.data.data.name, value: newBankId }
        const newState = {}
        newState[newBankId] = selectData

        forms.verification_field_bank_account_number.setValue('verification_field_bank_name', selectData)
        setCurrentBank(selectData)
        setSelectedBanks((prevState) => {
          return { ...prevState, ...newState }
        })
      }
    }
  };

  /**
   * Bank lookup using API
   * @param {string} routingNumber - The routing number to look up
   */
  const lookupBankName = async (routingNumber) => {
    if (!routingNumber || routingNumber.length !== 9 || !/^\d+$/.test(routingNumber)) {
      return;
    }

    setBankLookupLoading(true);
    try {
        requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/bank_name/${routingNumber}`);
      const response = await requests.get();

      if (response.status === 200 && response.data) {
        const bankData = response.data.data;
        // Create bank object
        const bankObject = {
          label: bankData.bank_name || 'Unknown Bank',
          value: bankData.bank_id || 'unknown'
        };

        // Set in form and state
        forms.verification_field_bank_account_number.setValue('verification_field_bank_name', bankObject);
        setCurrentBank(bankObject);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        dispatch(setNotification({ type: 'warning', body: 'Bank not found for this routing number' }));
        // Clear the bank name field
        forms.verification_field_bank_account_number.setValue('verification_field_bank_name', '');
        setCurrentBank(null);
      } else {
        dispatch(setNotification({ type: 'error', body: 'Unable to verify bank. Please try again.' }));
        // Clear the bank name field
        forms.verification_field_bank_account_number.setValue('verification_field_bank_name', '');
        setCurrentBank(null);
      }
    } finally {
      setBankLookupLoading(false);
    }
  };

  // Debounced version of lookupBankName to avoid excessive API calls
  const debouncedLookupBankName = useMemo(() => {
    let timeoutId;
    return (routingNumber) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        lookupBankName(routingNumber);
      }, 500); // 500ms debounce
    };
  });

  /**
   * If the customer data changes, we need to make sure to re-render the SSN into the correct field. This is only
   *  needed for SSN because it is one of the few fields we can edit outside the verification process via the quick
   *  action. We are using default value to ensure we account for verification fields if they exist.
   */
  useEffect(() => {
    if (Object.keys(customerData).length > 0) {
      forms.verification_field_ssn_last_four.setValue('verification_field_ssn_last_four', parseDefaultValue('verification_field_ssn_last_four'))
      forms.verification_field_bank_account_number.setValue('verification_field_bank_account_number', parseDefaultValue('verification_field_bank_account_number'))
      forms.verification_field_bank_account_number.setValue('verification_field_bank_routing_number', parseDefaultValue('verification_field_bank_routing_number'))
      // Handle bank name dropdown
      const bankNameValue = parseDefaultValue('verification_field_bank_name')
      if (bankNameValue) {
        setBank(bankNameValue)
      }
      // Handle bank account type dropdown
      const bankSavingsValue = parseDefaultValue('verification_field_is_bank_savings')
      if (bankSavingsValue !== '' && bankSavingsValue !== null && bankSavingsValue !== undefined) {
        const foundOption = [{ label: 'Savings', value: 1 }, { label: 'Checking', value: 0 }].find(
          (option) => option.value == bankSavingsValue
        )
        forms.verification_field_bank_account_number.setValue(
          'verification_field_is_bank_savings',
          foundOption ?? ''
        )
      }
      // Handle online banking dropdown
      const onlineBankingValue = parseDefaultValue('verification_field_is_online_banking')
      if (onlineBankingValue !== '' && onlineBankingValue !== null && onlineBankingValue !== undefined) {
        const foundOption = [{ label: 'Yes', value: 1 }, { label: 'No', value: 0 }].find(
          (option) => option.value == onlineBankingValue
        )
        forms.verification_field_bank_account_number.setValue(
          'verification_field_is_online_banking',
          foundOption ?? ''
        )
      }
      // Handle direct deposit dropdown
      const directDepositValue = parseDefaultValue('verification_field_is_direct_deposit')
      if (directDepositValue !== '' && directDepositValue !== null && directDepositValue !== undefined) {
        const foundOption = [{ label: 'Yes', value: 1 }, { label: 'No', value: 0 }].find(
          (option) => option.value == directDepositValue
        )
        forms.verification_field_bank_account_number.setValue(
          'verification_field_is_direct_deposit',
          foundOption ?? ''
        )
      }
    }
  }, [customerData])

  // Used for Non System Dropdowns
  const parseLabel = ((field) => {
    let option = field.name;
    if (field.label) {
      option = field.label;
    }
    return option
  })

  // Used for Non System Dropdowns
  const parseValue = ((field) => {
    let option = field.id;
    if (field.value) {
      option = field.value;
    }
    return option
  })

  /**
   * TODO: Tech Debt: Look into type casting so we can use the stricter === comparison operator. For now to avoid
   *  breaking the page we musth use != or == not === or !==
   *
   * NOTE: parseDefaultValue returns either the value or '' (empty string) if there is no value. If the value is falsy,
   *  for example a boolean = 0 then != '' would evaluate to false instead of true. In those cases I have updated to !== instead of !=.
   */
  useEffect(() => {
    forms.verification_field_bank_account_number.setValue('verification_field_bank_account_number', parseDefaultValue('verification_field_bank_account_number'))

    if (parseDefaultValue('verification_field_income_type_dropdown_option_id') !== '') {
      forms.verification_field_income_type_dropdown_option_id.setValue('verification_field_income_type_dropdown_option_id', sourceOfIncomeOptions.find(
        (option) => option.value == parseDefaultValue('verification_field_income_type_dropdown_option_id')
      ) ?? '')
    }
    if (parseDefaultValue('verification_field_is_pay_before_holiday') !== '') {
      forms.verification_field_is_pay_before_holiday.setValue('verification_field_is_pay_before_holiday', [{ label: 'Before', value: 1 }, { label: 'After', value: 0 }].find(
        (option) => option.value == parseDefaultValue('verification_field_is_pay_before_holiday')
      ) ?? '')
    }

    if (parseDefaultValue('verification_field_is_automatic_payments') !== '') {
      forms.verification_field_is_automatic_payments.setValue('verification_field_is_automatic_payments', [{ label: 'Yes', value: 1 }, { label: 'No', value: 0 }].find(
        (option) => option.value == parseDefaultValue('verification_field_is_automatic_payments')
      ) ?? '')
    }

    if (parseDefaultValue('verification_field_is_confirm_pay_dates') !== '') {
      forms.verification_field_is_confirm_pay_dates.setValue('verification_field_is_confirm_pay_dates', [{ label: 'Yes', value: 1 }, { label: 'No', value: 0 }].find(
        (option) => option.value == parseDefaultValue('verification_field_is_confirm_pay_dates')
      ) ?? '')
    }

    // Bank dropdown values are now handled by Controller defaultValue logic

    // check if pay frequency has value
    if (parseDefaultValue('verification_field_pay_frequency_dropdown_option_id') !== '') {
      const val = payFrequencyOptions.find(
        (option) => option.value == parseDefaultValue('verification_field_pay_frequency_dropdown_option_id')
      )
      forms.verification_field_pay_frequency_dropdown_option_id.setValue('verification_field_pay_frequency_dropdown_option_id', val ?? '')
      setPayFrequencyValue(val?.label)

      // if pay frequency is weekly
      if (parseDefaultValue('verification_field_pay_first_day_of_week_dropdown_option_id') !== "") {
        forms.verification_field_pay_frequency_dropdown_option_id.setValue('verification_field_pay_first_day_of_week_dropdown_option_id', dayOfWeekOptions.find(
          (option) => option.value == parseDefaultValue('verification_field_pay_first_day_of_week_dropdown_option_id')
        ) ?? '')
      }

      // if pay frequency is biweekly
      if (parseDefaultValue('verification_field_next_pay_date') !== "") {
        forms.verification_field_next_pay_date.setValue(parseDefaultValue('verification_field_next_pay_date'))
      }

      // if pay frequency is semi-monthly and specific days
      if (parseDefaultValue('verification_field_pay_frequency_dropdown_option_id') == dropdownOptions.find((option) => option?.code_key == 'pay_frequency_semimonthly')?.id
        && parseDefaultValue('verification_field_pay_first_day_of_month_dropdown_option_id') != ""
        && parseDefaultValue('verification_field_pay_second_day_of_month_dropdown_option_id') != ""
        && parseDefaultValue('verification_field_pay_first_day_of_month_dropdown_option_id') != null
        && parseDefaultValue('verification_field_pay_second_day_of_month_dropdown_option_id') != null) {
        setPayFrequencyValueOption('semimonthly-option1')
        forms.semi_monthly_pay_frequency.setValue('semi_monthly_pay_frequency', [{ label: 'Specific Days', value: 'semimonthly-option1' }, { label: 'Week Day and Week of Month', value: 'semimonthly-option2' }].find(
          (option) => option.value == 'semimonthly-option1'
        ) ?? '')
      }

      // if pay frequency is semi-monthly and week day and week of month
      if (parseDefaultValue('verification_field_pay_frequency_dropdown_option_id') == dropdownOptions.find((option) => option?.code_key == 'pay_frequency_semimonthly')?.id
        && parseDefaultValue('verification_field_pay_first_week_of_month_dropdown_option_id') != ""
        && parseDefaultValue('verification_field_pay_first_day_of_week_dropdown_option_id') != ""
        && parseDefaultValue('verification_field_pay_second_week_of_month_dropdown_option_id') != ""
        && parseDefaultValue('verification_field_pay_second_day_of_week_dropdown_option_id') != ""
        && parseDefaultValue('verification_field_pay_first_week_of_month_dropdown_option_id') != null
        && parseDefaultValue('verification_field_pay_first_day_of_week_dropdown_option_id') != null
        && parseDefaultValue('verification_field_pay_second_week_of_month_dropdown_option_id') != null
        && parseDefaultValue('verification_field_pay_second_day_of_week_dropdown_option_id') != null) {
        setPayFrequencyValueOption('semimonthly-option2')
        forms.semi_monthly_pay_frequency.setValue('semi_monthly_pay_frequency', [{ label: 'Specific Days', value: 'semimonthly-option1' }, { label: 'Week Day and Week of Month', value: 'semimonthly-option2' }].find(
          (option) => option.value == 'semimonthly-option2'
        ) ?? '')
      }

      // if pay frequency is monthly and Pay Day of Months
      if (parseDefaultValue('verification_field_pay_frequency_dropdown_option_id') != ""
        && parseDefaultValue('verification_field_pay_frequency_dropdown_option_id') == dropdownOptions.find((option) => option?.code_key == 'pay_frequency_monthly')?.id
        && parseDefaultValue('verification_field_pay_first_day_of_month_dropdown_option_id') != ""
        && parseDefaultValue('verification_field_pay_first_day_of_month_dropdown_option_id') != null) {
        setPayFrequencyValueOption('monthly-option1')
        forms.monthly_pay_frequency.setValue('monthly_pay_frequency', [{ label: 'Pay Day of Months', value: 'monthly-option1' }, { label: 'Week Day and Week of Month', value: 'monthly-option2' }].find(
          (option) => option.value == 'monthly-option1'
        ) ?? '')
      }

      // if pay frequency is monthly and week day and week of month
      if (parseDefaultValue('verification_field_pay_frequency_dropdown_option_id') != ""
        && parseDefaultValue('verification_field_pay_frequency_dropdown_option_id') == dropdownOptions.find((option) => option?.code_key == 'pay_frequency_monthly')?.id
        && parseDefaultValue('verification_field_pay_first_week_of_month_dropdown_option_id') != ""
        && parseDefaultValue('verification_field_pay_first_day_of_week_dropdown_option_id') != ""
        && parseDefaultValue('verification_field_pay_first_week_of_month_dropdown_option_id') != null
        && parseDefaultValue('verification_field_pay_first_day_of_week_dropdown_option_id') != null) {
        setPayFrequencyValueOption('monthly-option2')
        forms.monthly_pay_frequency.setValue('monthly_pay_frequency', [{ label: 'Pay Day of Months', value: 'monthly-option1' }, { label: 'Week Day and Week of Month', value: 'monthly-option2' }].find(
          (option) => option.value == 'monthly-option2'
        ) ?? '')
      }

      // some additional default values for pay frequency
      if (parseDefaultValue('verification_field_pay_first_day_of_month_dropdown_option_id') != "") {
        forms.verification_field_pay_frequency_dropdown_option_id.setValue('verification_field_pay_first_day_of_month_dropdown_option_id', dayOfMonthOptions.find(
          (option) => option.value == parseDefaultValue('verification_field_pay_first_day_of_month_dropdown_option_id')
        ) ?? '')
      }

      if (parseDefaultValue('verification_field_pay_second_day_of_month_dropdown_option_id') != "") {
        forms.verification_field_pay_frequency_dropdown_option_id.setValue('verification_field_pay_second_day_of_month_dropdown_option_id', dayOfMonthOptions.find(
          (option) => option.value == parseDefaultValue('verification_field_pay_second_day_of_month_dropdown_option_id')
        ) ?? '')
      }

      if (parseDefaultValue('verification_field_pay_first_week_of_month_dropdown_option_id') != "") {
        forms.verification_field_pay_frequency_dropdown_option_id.setValue('verification_field_pay_first_week_of_month_dropdown_option_id', weekOfMonthOptions.find(
          (option) => option.value == parseDefaultValue('verification_field_pay_first_week_of_month_dropdown_option_id')
        ) ?? '')
      }

      if (parseDefaultValue('verification_field_pay_second_week_of_month_dropdown_option_id') != "") {
        forms.verification_field_pay_frequency_dropdown_option_id.setValue('verification_field_pay_second_week_of_month_dropdown_option_id', weekOfMonthOptions.find(
          (option) => option.value == parseDefaultValue('verification_field_pay_second_week_of_month_dropdown_option_id')
        ) ?? '')
      }

      if (parseDefaultValue('verification_field_pay_second_day_of_week_dropdown_option_id') != "") {
        forms.verification_field_pay_frequency_dropdown_option_id.setValue('verification_field_pay_second_day_of_week_dropdown_option_id', dayOfWeekOptions.find(
          (option) => option.value == parseDefaultValue('verification_field_pay_second_day_of_week_dropdown_option_id')
        ) ?? '')
      }
    }
  }, [])

  useEffect(() => {
    forms.verification_field_bank_account_number.setValue('verification_field_bank_account_number', parseDefaultValue('verification_field_bank_account_number'))
    // Bank dropdown values are now handled by Controller defaultValue logic
  }, [verificationFields])

  useEffect(() => {
    setIncomeTypeSelection(sourceOfIncome?.dropdown_options.find((o) => o.id == parseDefaultValue('verification_field_income_type_dropdown_option_id'))?.code_key)
  }, [verificationFields])

  // verify if all fields are completed
  const isVerificationCompleted = (verificationFields, processingStatusCompletedId) => {
    // Only require verificationId if we have no verification fields
    if (!verificationId && (!verificationFields || Object.keys(verificationFields).length === 0)) {
      return false
    }
    // If verificationFields is empty or null, return false (button disabled)
    if (!verificationFields || Object.keys(verificationFields).length === 0) {
      return false
    }
    if (!verificationFields['verification_field_first_name'] || verificationFields['verification_field_first_name']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
      return false
    } else if (!verificationFields['verification_field_last_name'] || verificationFields['verification_field_last_name']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
      return false
    } else if (!verificationFields['verification_field_ssn_last_four'] || verificationFields['verification_field_ssn_last_four']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
      return false
    } else if (!verificationFields['verification_field_birth_date'] || verificationFields['verification_field_birth_date']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
      return false
    } else if (!verificationFields['verification_field_home_phone_number'] || verificationFields['verification_field_home_phone_number']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
      return false
    } else if (!verificationFields['verification_field_cell_phone_number'] || verificationFields['verification_field_cell_phone_number']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
      return false
    } else if (!verificationFields['verification_field_email_address'] || verificationFields['verification_field_email_address']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
      return false
    } else if (!verificationFields['verification_field_bank_name'] || verificationFields['verification_field_bank_name']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
      return false
    } else if (!verificationFields['verification_field_bank_account_number'] || verificationFields['verification_field_bank_account_number']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
      return false
    } else if (!verificationFields['verification_field_bank_routing_number'] || verificationFields['verification_field_bank_routing_number']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
      return false
    } else if (!verificationFields['verification_field_is_online_banking'] || verificationFields['verification_field_is_online_banking']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
      return false
    } else if (!verificationFields['verification_field_income_type_dropdown_option_id'] || verificationFields['verification_field_income_type_dropdown_option_id']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
      return false
    } else if (sourceOfIncome?.dropdown_options.find((o) => o.id == parseDefaultValue('verification_field_income_type_dropdown_option_id'))?.code_key == "income_type_employed" && (!verificationFields['verification_field_employer_name'] || verificationFields['verification_field_employer_name']?.processing_status_dropdown_option_id !== processingStatusCompletedId)) {
      return false
    } else if (sourceOfIncome?.dropdown_options.find((o) => o.id == parseDefaultValue('verification_field_income_type_dropdown_option_id'))?.code_key == "income_type_employed" && (!verificationFields['verification_field_job_title'] || verificationFields['verification_field_job_title']?.processing_status_dropdown_option_id !== processingStatusCompletedId)) {
      return false
    } else if (sourceOfIncome?.dropdown_options.find((o) => o.id == parseDefaultValue('verification_field_income_type_dropdown_option_id'))?.code_key == "income_type_employed" && (!verificationFields['verification_field_employer_phone'] || verificationFields['verification_field_employer_phone']?.processing_status_dropdown_option_id !== processingStatusCompletedId)) {
      return false
    } else if (!verificationFields['verification_field_is_direct_deposit'] || verificationFields['verification_field_is_direct_deposit']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
      return false
    } else if (!verificationFields['verification_field_is_bank_savings'] || verificationFields['verification_field_is_bank_savings']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
      return false
    } else if (!verificationFields['verification_field_net_monthly_income'] || verificationFields['verification_field_net_monthly_income']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
      return false
    } else if (!verificationFields['verification_field_is_pay_before_holiday'] || verificationFields['verification_field_is_pay_before_holiday']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
      return false
    } else if (!verificationFields['verification_field_is_automatic_payments'] || verificationFields['verification_field_is_automatic_payments']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
      return false
    } else if (!verificationFields['verification_field_is_confirm_pay_dates'] || verificationFields['verification_field_is_confirm_pay_dates']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
      return false
    }

    if (!verificationFields['verification_field_pay_frequency_dropdown_option_id'] || verificationFields['verification_field_pay_frequency_dropdown_option_id']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
      return false
    }

    if (parseDefaultValue('verification_field_pay_frequency_dropdown_option_id') === dropdownOptions.find((option) => option?.code_key === 'pay_frequency_weekly')?.id) {
      if (!verificationFields['verification_field_pay_first_day_of_week_dropdown_option_id'] || verificationFields['verification_field_pay_first_day_of_week_dropdown_option_id']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
        return false
      }
    }

    if (parseDefaultValue('verification_field_pay_frequency_dropdown_option_id') == dropdownOptions.find((option) => option?.code_key === 'pay_frequency_biweekly')?.id) {
      if (!verificationFields['verification_field_next_pay_date'] || verificationFields['verification_field_next_pay_date']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
        return false
      }
    }

    if (parseDefaultValue('verification_field_pay_frequency_dropdown_option_id') === dropdownOptions.find((option) => option?.code_key === 'pay_frequency_semimonthly')?.id) {
      if (payFrequencyValueOption === "semimonthly-option1") {
        if (!verificationFields['verification_field_pay_first_day_of_month_dropdown_option_id'] || verificationFields['verification_field_pay_first_day_of_month_dropdown_option_id']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
          return false
        } else if (!verificationFields['verification_field_pay_second_day_of_month_dropdown_option_id'] || verificationFields['verification_field_pay_second_day_of_month_dropdown_option_id']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
          return false
        }
      } else if (payFrequencyValueOption === "semimonthly-option2") {
        if (!verificationFields['verification_field_pay_first_week_of_month_dropdown_option_id'] || verificationFields['verification_field_pay_first_week_of_month_dropdown_option_id']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
          return false
        } else if (!verificationFields['verification_field_pay_first_day_of_week_dropdown_option_id'] || verificationFields['verification_field_pay_first_day_of_week_dropdown_option_id']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
          return false
        } else if (!verificationFields['verification_field_pay_second_week_of_month_dropdown_option_id'] || verificationFields['verification_field_pay_second_week_of_month_dropdown_option_id']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
          return false
        } else if (!verificationFields['verification_field_pay_second_day_of_week_dropdown_option_id'] || verificationFields['verification_field_pay_second_day_of_week_dropdown_option_id']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
          return false
        }
      }
    }

    if (parseDefaultValue('verification_field_pay_frequency_dropdown_option_id') === dropdownOptions.find((option) => option?.code_key === 'pay_frequency_monthly')?.id) {
      if (payFrequencyValueOption === "monthly-option1") {
        if (!verificationFields['verification_field_pay_first_day_of_month_dropdown_option_id'] || verificationFields['verification_field_pay_first_day_of_month_dropdown_option_id']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
          return false
        }
      } else if (payFrequencyValueOption === "monthly-option2") {
        if (!verificationFields['verification_field_pay_first_week_of_month_dropdown_option_id'] || verificationFields['verification_field_pay_first_week_of_month_dropdown_option_id']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
          return false
        } else if (!verificationFields['verification_field_pay_first_day_of_week_dropdown_option_id'] || verificationFields['verification_field_pay_first_day_of_week_dropdown_option_id']?.processing_status_dropdown_option_id !== processingStatusCompletedId) {
          return false
        }
      }
    }
    return true;
  }

  /**
   * Prefill whatever values we can for the agent.
   * If we have the verification responses information then use that, if not then try to use the customer, otherwise leave blank
   * @param verification_field
   * @returns {*|string}
   */
  const parseDefaultValue = (verification_field) => {
    // Strip off 'verification_field_'
    let fieldValue = ""
    const fieldName = verification_field.substring(19)
    if (verificationFields[verification_field]) {
      fieldValue = verificationFields[verification_field].field_value
      if (fieldName === 'bank_account_number') {
        fieldValue = fieldValue.slice(-4);
        const max_length = 10
        if (fieldValue) {
          fieldValue = '#'.repeat(max_length - 4) + fieldValue
        } else {
          fieldValue = '#'.repeat(max_length)
        }
      }
    } else if (fieldName in customerData) {
      if (fieldName === 'bank_account_number') {
        const last_four = customerData[fieldName]
        const max_length = 10
        if (last_four) {
          fieldValue = '#'.repeat(max_length - 4) + last_four
        } else {
          fieldValue = '#'.repeat(max_length)
        }
      } else {
        fieldValue = customerData[fieldName]
      }
    } else {
      fieldValue = ''
    }
    // Ensure we never return null or undefined - convert to empty string
    const result = fieldValue ?? ''
    return result
  }

  const renderEditButton = (verification_field) => {
    return (
      <div className="is-flex is-align-items-center auto-fit-column verification-margin">
        <button
          type="button"
          className="button has-background-grey has-text-light is-small btn-verification"
          onClick={() => handleEditAndCloseClick(verification_field, true)}
          disabled={!startVerification}
        >
          Edit
        </button>
      </div>
    )
  }

  const renderSaveCancelButton = (verification_field) => {
    return (
      <>
        <div className="is-align-items-center auto-fit-column">
          <button
            type="button"
            className="button has-background-grey has-text-light is-small btn-verification"
            onClick={() => handleEditAndCloseClick(verification_field, false, true)}
            disabled={!startVerification || verificationFieldsObjectState[verification_field]?.isLoading}
          >
            Cancel
          </button>
        </div>
        <div className="is-align-items-center auto-fit-column verification-margin">
          <button
            type="submit"
            className="button is-link has-text-light is-small btn-verification"
            disabled={!startVerification || verificationFieldsObjectState[verification_field]?.isLoading}
          >
            {verificationFieldsObjectState[verification_field]?.isLoading ? <FontAwesomeIcon icon={faRefresh} spin /> : "Save"}
          </button>
        </div>
      </>
    )
  }

  const renderVerifyButton = (verification_field) => {
    return (
      <div className="is-align-items-center auto-fit-column verification-margin">
        <button type="submit" className="button has-background-grey has-text-light is-small btn-verification" disabled={!startVerification || verificationFieldsObjectState[verification_field]?.isLoading}>{verificationFieldsObjectState[verification_field]?.isLoading ? <FontAwesomeIcon icon={faRefresh} spin /> : "Verify"}</button>
      </div>
    )
  }

  const disableField = (verification_field) => {
    let verificationComplete = false
    if (verificationFields[verification_field]) {
      // Special case for income type
      if (verification_field == "verification_field_income_type_dropdown_option_id" && sourceOfIncome?.dropdown_options.find((o) => o.id == parseDefaultValue('verification_field_income_type_dropdown_option_id'))?.code_key == "income_type_employed") {
        const processingStatusIncome = dropdownOptions.filter((o) => o.id === verificationFields['verification_field_income_type_dropdown_option_id']?.processing_status_dropdown_option_id)[0]?.code_key
        const processingStatusEmpName = dropdownOptions.filter((o) => o.id === verificationFields['verification_field_employer_name']?.processing_status_dropdown_option_id)[0]?.code_key
        const processingStatusEmpPhone = dropdownOptions.filter((o) => o.id === verificationFields['verification_field_employer_phone']?.processing_status_dropdown_option_id)[0]?.code_key
        const processingStatusPosition = dropdownOptions.filter((o) => o.id === verificationFields['verification_field_job_title']?.processing_status_dropdown_option_id)[0]?.code_key
        if (processingStatusIncome === 'processing_status_completed' &&
          processingStatusEmpName === 'processing_status_completed' &&
          processingStatusEmpPhone === 'processing_status_completed' &&
          processingStatusPosition === 'processing_status_completed') {
          verificationComplete = true
        }
      } else if ([
        'verification_field_bank_account_number',
      ].includes(verification_field)) {
        const bankFields = [
          'verification_field_bank_account_number',
          'verification_field_bank_routing_number',
          'verification_field_bank_name',
          'verification_field_is_bank_savings',
          'verification_field_is_online_banking',
          'verification_field_is_direct_deposit',
        ];
        const allBankFieldsComplete = bankFields.every(
          (field) => {
            const status = dropdownOptions.filter((o) => o.id === verificationFields[field]?.processing_status_dropdown_option_id)[0]?.code_key;
            return status === 'processing_status_completed';
          }
        );
        if (allBankFieldsComplete) {
          verificationComplete = true;
        }
      } else {
        const processingStatus = dropdownOptions.filter((o) => o.id === verificationFields[verification_field]?.processing_status_dropdown_option_id)[0]?.code_key
        if (processingStatus === 'processing_status_completed') {
          verificationComplete = true
        }
      }
      // TODO: Special case for pay frequency
    }

    return !(!verificationComplete || verificationFieldsObjectState[verification_field]?.isEditing == true);
  }

  const renderStatusActions = (verification_field) => {
    let verificationComplete = false
    if (verificationFields[verification_field]) {
      // Special case for income type
      if (verification_field == "verification_field_income_type_dropdown_option_id" && sourceOfIncome?.dropdown_options.find((o) => o.id == parseDefaultValue('verification_field_income_type_dropdown_option_id'))?.code_key == "income_type_employed") {
        const processingStatusIncome = dropdownOptions.filter((o) => o.id === verificationFields['verification_field_income_type_dropdown_option_id']?.processing_status_dropdown_option_id)[0]?.code_key
        const processingStatusEmpName = dropdownOptions.filter((o) => o.id === verificationFields['verification_field_employer_name']?.processing_status_dropdown_option_id)[0]?.code_key
        const processingStatusEmpPhone = dropdownOptions.filter((o) => o.id === verificationFields['verification_field_employer_phone']?.processing_status_dropdown_option_id)[0]?.code_key
        const processingStatusPosition = dropdownOptions.filter((o) => o.id === verificationFields['verification_field_job_title']?.processing_status_dropdown_option_id)[0]?.code_key
        if (processingStatusIncome === 'processing_status_completed' &&
          processingStatusEmpName === 'processing_status_completed' &&
          processingStatusEmpPhone === 'processing_status_completed' &&
          processingStatusPosition === 'processing_status_completed') {
          verificationComplete = true
        }
      } else if ([
        'verification_field_bank_account_number',
      ].includes(verification_field)) {
        const bankFields = [
          'verification_field_bank_account_number',
          'verification_field_bank_routing_number',
          'verification_field_bank_name',
          'verification_field_is_bank_savings',
          'verification_field_is_online_banking',
          'verification_field_is_direct_deposit',
        ];
        const allBankFieldsComplete = bankFields.every(
          (field) => {
            const status = dropdownOptions.filter((o) => o.id === verificationFields[field]?.processing_status_dropdown_option_id)[0]?.code_key;
            return status === 'processing_status_completed';
          }
        );
        if (allBankFieldsComplete) {
          verificationComplete = true;
        }
      } else {
        const processingStatus = dropdownOptions.filter((o) => o.id === verificationFields[verification_field]?.processing_status_dropdown_option_id)[0]?.code_key
        if (processingStatus === 'processing_status_completed') {
          verificationComplete = true
        }
      }
      // TODO: Special case for pay frequency
    }

    let action = ''
    if (verificationFields[verification_field]) {
      let actionSource = dropdownOptions.filter((o) => o.id === verificationFields[verification_field].action_source_dropdown_option_id)[0]?.code_key
      if (actionSource === 'action_source_admin') {
        actionSource = verificationFields[verification_field].updated_by
      } else if (actionSource === 'action_source_app_ssv') {
        actionSource = 'SSV';
      } else {
        actionSource = verificationFields[verification_field].updated_by
      }
      const updatedAt = FormatISODateTime(verificationFields[verification_field].updated_at)
      action = `${actionSource} ${updatedAt}`
    }

    let button = '';
    if (!verificationComplete) {
      button = renderVerifyButton(verification_field)
    } else if (verificationFieldsObjectState[verification_field]?.isEditing == true) {
      button = renderSaveCancelButton(verification_field)
    } else {
      button = renderEditButton(verification_field)
    }

    return (
      <>
        <div className="is-flex is-align-items-center auto-fit-column verification-margin">
          {button}
          {verificationComplete && startVerification && verificationId == verificationFields[verification_field]?.verification_id && (
            <div className="is-align-items-center auto-fit-column">
              <button
                type="submit"
                className="button is-danger has-text-light is-small px-2"
                disabled={!startVerification || verificationFieldsObjectState[verification_field]?.isLoading}
                onClick={() => setBtnUndoVerify(true)}
              >
                {verificationFieldsObjectState[verification_field]?.isLoading ? <FontAwesomeIcon icon={faRefresh} spin /> : <FontAwesomeIcon icon={faUndo} className="is-size-7" />}
              </button>
            </div>
          )}
          <div className="is-flex is-align-items-center auto-fit-column verification-icon-col">
            {verificationComplete ? <SuccessIcon /> : <DeclineIcon />}
          </div>
        </div>
        <div className="is-flex is-align-items-center auto-fit-column verification-margin">
          <p className="is-italic" style={{ color: 'grey' }}>{action}</p>
        </div>
      </>
    )
  }

  return (
    <div id="step1">
      <div className="columns lox-detail mt-1 mx-1">
        <div className="column is-three-quarters">
          <div className="columns mb-0">
            <div
              className="column is-flex is-flex-direction-column is-justify-content-center is-align-items-flex-start"
            >
              <div className="title is-4 has-text-primary is-flex" style={{ whiteSpace: 'nowrap' }}>
                Verification Step 1: Verify Data
              </div>
            </div>
          </div>
          <form onSubmit={forms.verification_field_first_name.handleSubmit(onSubmit)}>
            <div className="columns mb-0 lox-verification-cell">
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  First Name:
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`Am I speaking with ${parseDefaultValue('verification_field_first_name')} ${parseDefaultValue('verification_field_last_name')}?`}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <Controller
                  name="verification_field_first_name"
                  control={forms.verification_field_first_name.control}
                  defaultValue={parseDefaultValue('verification_field_first_name')}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <input
                      {...field}
                      className="input is-small"
                      type="text"
                      {...forms.verification_field_first_name.register('verification_field_first_name')}
                      disabled={startVerification === false || disableField('verification_field_first_name')}
                      onChange={(val) => field.onChange(val)}
                    />
                  )}
                />
              </div>
              {renderStatusActions('verification_field_first_name')}
            </div>
          </form>

          <form onSubmit={forms.verification_field_last_name.handleSubmit(onSubmit)}>
            <div className="columns mb-0 lox-verification-cell">
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Last Name:
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`Am I speaking with ${parseDefaultValue('verification_field_first_name')} ${parseDefaultValue('verification_field_last_name')}?`}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <Controller
                  name="verification_field_last_name"
                  control={forms.verification_field_last_name.control}
                  defaultValue={parseDefaultValue('verification_field_last_name')}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <input
                      {...field}
                      className="input is-small"
                      type="text"
                      {...forms.verification_field_last_name.register('verification_field_last_name')}
                      disabled={startVerification === false || disableField('verification_field_last_name')}
                      onChange={(val) => field.onChange(val)}
                    />
                  )}
                />
              </div>
              {renderStatusActions('verification_field_last_name')}
            </div>
          </form>

          <form onSubmit={forms.verification_field_ssn_last_four.handleSubmit(onSubmit)}>
            <div className="columns mb-0 lox-verification-cell">
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Last 4 SSN:
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`For security purposes, can you verify the last four digits of your social and full date of birth please? ${parseDefaultValue('verification_field_ssn_last_four')}`}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <Controller
                  name="verification_field_ssn_last_four"
                  control={forms.verification_field_ssn_last_four.control}
                  defaultValue={parseDefaultValue('verification_field_ssn_last_four')}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <input
                      {...field}
                      className="input is-small"
                      type="text"
                      {...forms.verification_field_ssn_last_four.register('verification_field_ssn_last_four')}
                      disabled={startVerification === false || disableField('verification_field_ssn_last_four')}
                      onChange={(val) => field.onChange(val)}
                    />
                  )}
                />
              </div>
              {renderStatusActions('verification_field_ssn_last_four')}
              <ViewSensitivePiiButtons customerId={customerData.id} type={'full_ssn'} style={{ marginTop: '6px' }} />
            </div>
          </form>

          <form onSubmit={forms.verification_field_birth_date.handleSubmit(onSubmit)}>
            <div className="columns mb-0 lox-verification-cell">
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Date of Birth:
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`For security purposes, can you verify the last four digits of your social and full date of birth please?`}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <Controller
                  name="verification_field_birth_date"
                  control={forms.verification_field_birth_date.control}
                  defaultValue={parseDefaultValue('verification_field_birth_date')}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <input
                      {...field}
                      className="input is-small"
                      type="date"
                      {...forms.verification_field_birth_date.register('verification_field_birth_date')}
                      disabled={startVerification === false || disableField('verification_field_birth_date')}
                      onChange={(val) => field.onChange(val)}
                    />
                  )}
                />
              </div>
              {renderStatusActions('verification_field_birth_date')}
            </div>
          </form>

          <form onSubmit={forms.verification_field_home_phone_number.handleSubmit(onSubmit)}>
            <div className="columns mb-0 lox-verification-cell">
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Home Phone Number:
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`Can you please verify your home phone and cell phone numbers?`}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <Controller
                  name="verification_field_home_phone_number"
                  control={forms.verification_field_home_phone_number.control}
                  defaultValue={parseDefaultValue('verification_field_home_phone_number')}
                  render={({ field }) => (
                    <input
                      {...field}
                      className="input is-small"
                      type="text"
                      {...forms.verification_field_home_phone_number.register('verification_field_home_phone_number')}
                      disabled={startVerification === false || disableField('verification_field_home_phone_number')}
                      onChange={(val) => field.onChange(val)}
                    />
                  )}
                />
              </div>
              {renderStatusActions('verification_field_home_phone_number')}
            </div>
          </form>

          <form onSubmit={forms.verification_field_cell_phone_number.handleSubmit(onSubmit)}>
            <div className="columns mb-0 lox-verification-cell">
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Cell Phone Number:
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`Can you please verify your home phone and cell phone numbers?`}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <Controller
                  name="verification_field_cell_phone_number"
                  control={forms.verification_field_cell_phone_number.control}
                  defaultValue={parseDefaultValue('verification_field_cell_phone_number')}
                  render={({ field }) => (
                    <input
                      {...field}
                      className="input is-small"
                      type="text"
                      {...forms.verification_field_cell_phone_number.register('verification_field_cell_phone_number')}
                      disabled={startVerification === false || disableField('verification_field_cell_phone_number')}
                      onChange={(val) => field.onChange(val)}
                    />
                  )}
                />
              </div>
              {renderStatusActions('verification_field_cell_phone_number')}
            </div>
          </form>

          <form onSubmit={forms.verification_field_email_address.handleSubmit(onSubmit)}>
            <div className="columns mb-0 lox-verification-cell">
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Email Address:
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`I have your email address as ${parseDefaultValue('verification_field_email_address')} is this correct?`}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <Controller
                  name="verification_field_email_address"
                  control={forms.verification_field_email_address.control}
                  defaultValue={parseDefaultValue('verification_field_email_address')}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <input
                      {...field}
                      className="input is-small"
                      type="text"
                      {...forms.verification_field_email_address.register('verification_field_email_address')}
                      disabled={startVerification === false || disableField('verification_field_email_address')}
                      onChange={(val) => field.onChange(val)}
                    />
                  )}
                />
              </div>
              {renderStatusActions('verification_field_email_address')}
            </div>
          </form>

          <div className="divider mb-4 my-2" />

          {/* START SOURCE OF INCOME */}
          <form onSubmit={forms.verification_field_income_type_dropdown_option_id.handleSubmit(onSourceOfIncomeSubmit)}>
            <div className="columns mb-0 lox-verification-cell">
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Primary Source of Income :
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`What is your primary source of income?`}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <div style={{ width: '100%' }}>
                  <Controller
                    name="verification_field_income_type_dropdown_option_id"
                    control={forms.verification_field_income_type_dropdown_option_id.control}
                    rules={{ required: true }}
                    defaultValue={''}
                    render={({ field }) => (
                      <Select
                        {...field}
                        name="verification_field_income_type_dropdown_option_id"
                        {...forms.verification_field_income_type_dropdown_option_id.register('verification_field_income_type_dropdown_option_id')}
                        classNames={reactSelectClassNames()}
                        options={sourceOfIncomeOptions}
                        isDisabled={startVerification === false || disableField('verification_field_income_type_dropdown_option_id')}
                        isSearchable={false}
                        onChange={(val) => {
                          setIncomeTypeSelection(val?.codeKey)
                          field.onChange({ label: val?.label, value: val?.value })
                        }}
                      />
                    )}
                  />
                </div>
              </div>
              {renderStatusActions('verification_field_income_type_dropdown_option_id')}
            </div>

            <div
              className={`columns mb-0 lox-verification-cell`}
            >
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Current Employer:
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`Who is your current employer?`}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <Controller
                  name="verification_field_employer_name"
                  control={forms.verification_field_income_type_dropdown_option_id.control}
                  defaultValue={parseDefaultValue('verification_field_employer_name')}
                  render={({ field }) => (
                    <input
                      {...field}
                      className="input is-small"
                      type="text"
                      {...forms.verification_field_income_type_dropdown_option_id.register('verification_field_employer_name')}
                      disabled={startVerification === false || disableField('verification_field_income_type_dropdown_option_id')}
                      onChange={(val) => field.onChange(val)}
                    />
                  )}
                />
              </div>
            </div>

            <div className={`columns mb-0 lox-verification-cell`}>
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Employer Phone Number:
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`Can you please verify your employers phone number?`}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <Controller
                  name="verification_field_employer_phone"
                  control={forms.verification_field_income_type_dropdown_option_id.control}
                  defaultValue={parseDefaultValue('verification_field_employer_phone')}
                  render={({ field }) => (
                    <input
                      {...field}
                      className="input is-small"
                      type="text"
                      {...forms.verification_field_income_type_dropdown_option_id.register('verification_field_employer_phone')}
                      disabled={startVerification === false || disableField('verification_field_income_type_dropdown_option_id')}
                      onChange={(val) => field.onChange(val)}
                    />
                  )}
                />
              </div>
            </div>

            <div
              className={`columns mb-0 lox-verification-cell`}
            >
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Position:
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`What is your position there?`}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <Controller
                  name="verification_field_job_title"
                  control={forms.verification_field_income_type_dropdown_option_id.control}
                  defaultValue={parseDefaultValue('verification_field_job_title')}
                  render={({ field }) => (
                    <input
                      {...field}
                      className="input is-small"
                      type="text"
                      {...forms.verification_field_income_type_dropdown_option_id.register('verification_field_job_title')}
                      disabled={startVerification === false || disableField('verification_field_income_type_dropdown_option_id')}
                      onChange={(val) => field.onChange(val)}
                    />
                  )}
                />
              </div>
            </div>
          </form>
          {/* END SOURCE OF INCOME */}

          <div className="divider mb-4 my-2" />

          <form id="net_monthly_income_form" onSubmit={forms.verification_field_net_monthly_income.handleSubmit(onSubmit)}>
            <div className="columns mb-0 lox-verification-cell">
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Net Monthly Income:
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`What is the total amount of your direct deposit(s) after taxes and deductions, per month?`}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <Controller
                  name="verification_field_net_monthly_income"
                  control={forms.verification_field_net_monthly_income.control}
                  defaultValue={parseDefaultValue('verification_field_net_monthly_income')}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <input
                      {...field}
                      className="input is-small"
                      type="text"
                      {...forms.verification_field_net_monthly_income.register('verification_field_net_monthly_income')}
                      disabled={startVerification === false || disableField('verification_field_net_monthly_income')}
                      onChange={(val) => field.onChange(val)}
                    />
                  )}
                />
              </div>
              {renderStatusActions('verification_field_net_monthly_income')}
            </div>
          </form>

          {/* START PAY FREQUENCY */}
          <form onSubmit={forms.verification_field_pay_frequency_dropdown_option_id.handleSubmit(onPayFrequencySubmit)}>
            <div className="columns mb-0 lox-verification-cell">
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Pay Frequency:
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`How often do you get paid?`}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <div style={{ width: '100%' }}>
                  <Controller
                    name="verification_field_pay_frequency_dropdown_option_id"
                    control={forms.verification_field_pay_frequency_dropdown_option_id.control}
                    rules={{ required: true }}
                    defaultValue={''}
                    render={({ field }) => (
                      <Select
                        {...field}
                        name="verification_field_pay_frequency_dropdown_option_id"
                        {...forms.verification_field_pay_frequency_dropdown_option_id.register('verification_field_pay_frequency_dropdown_option_id')}
                        classNames={reactSelectClassNames()}
                        options={payFrequencyOptions}
                        isDisabled={startVerification === false || disableField('verification_field_pay_frequency_dropdown_option_id')}
                        isSearchable={false}
                        onChange={(val) => {
                          field.onChange({ label: val?.label, value: val?.value })
                          setPayFrequencyValue(val?.label)
                        }}
                      />
                    )}
                  />
                </div>
              </div>
              {renderStatusActions('verification_field_pay_frequency_dropdown_option_id')}
            </div>

            {/* display this option if semi-monthly option is selected */}
            <div
              className={`columns mb-0 lox-verification-cell ${payFrequencyValue == 'Semi-Monthly' ? '' : 'is-hidden'}`}
            >
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Semi Monthly Option:
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <div style={{ width: '100%' }}>
                  <Controller
                    name="semi_monthly_pay_frequency"
                    control={forms.semi_monthly_pay_frequency.control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        name="semi_monthly_pay_frequency"
                        classNames={reactSelectClassNames()}
                        options={[{
                          label: 'Specific Days',
                          value: 'semimonthly-option1',
                        }, { label: 'Week Day and Week of Month', value: 'semimonthly-option2' }]}
                        isDisabled={startVerification === false || disableField('verification_field_pay_frequency_dropdown_option_id')}
                        isSearchable={false}
                        onChange={(val) => {
                          field.onChange({ label: val?.label, value: val?.value })
                          setPayFrequencyValueOption(val?.value)
                        }}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            {/* display this option if monthly option is selected */}
            <div className={`columns mb-0 lox-verification-cell ${payFrequencyValue == 'Monthly' ? '' : 'is-hidden'}`}>
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Monthly Option:
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <div style={{ width: '100%' }}>
                  <Controller
                    name="monthly_pay_frequency"
                    control={forms.monthly_pay_frequency.control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        name="monthly_pay_frequency"
                        classNames={reactSelectClassNames()}
                        options={[{
                          label: 'Pay Day of Months',
                          value: 'monthly-option1',
                        }, { label: 'Week Day and Week of Month', value: 'monthly-option2' }]}
                        isDisabled={startVerification === false || disableField('verification_field_pay_frequency_dropdown_option_id')}
                        isSearchable={false}
                        onChange={(val) => {
                          field.onChange({ label: val?.label, value: val?.value })
                          setPayFrequencyValueOption(val?.value)
                        }}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            {/* display this option if Semi-Monthly and Week Day and Week of Month choosen */}
            {/* display this option if Monthly and Week Day and Week of Month choosen */}
            <div
              className={`columns mb-0 lox-verification-cell ${((payFrequencyValue == 'Semi-Monthly' && payFrequencyValueOption == 'semimonthly-option2') || (payFrequencyValue == 'Monthly' && payFrequencyValueOption == 'monthly-option2')) ? '' : 'is-hidden'}`}
            >
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  {payFrequencyValue == 'Semi-Monthly' && payFrequencyValueOption == 'semimonthly-option2' && (
                    <>Week 1:</>
                  )}
                  {payFrequencyValue == 'Monthly' && payFrequencyValueOption == 'monthly-option2' && (<>Week:</>)}
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`
                    ${(payFrequencyValue == 'Semi-Monthly' && payFrequencyValueOption == 'semimonthly-option2') ? 'What days of each week do you get paid?' : ''}
                    ${(payFrequencyValue == 'Monthly' && payFrequencyValueOption == 'monthly-option2') ? 'What week of month and day of that week do you get paid?' : ''}
                  `}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <div style={{ width: '100%' }}>
                  <Controller
                    name="verification_field_pay_first_week_of_month_dropdown_option_id"
                    control={forms.verification_field_pay_frequency_dropdown_option_id.control}
                    defaultValue={verificationFields['verification_field_pay_first_week_of_month_dropdown_option_id'] ? {
                      label: verificationFields['verification_field_pay_first_week_of_month_dropdown_option_id'].field_value,
                      value: verificationFields['verification_field_pay_first_week_of_month_dropdown_option_id'].field_value,
                    } : ''}
                    render={({ field }) => (
                      <Select
                        {...field}
                        name="verification_field_pay_first_week_of_month_dropdown_option_id"
                        {...forms.verification_field_pay_frequency_dropdown_option_id.register('verification_field_pay_first_week_of_month_dropdown_option_id')}
                        classNames={reactSelectClassNames()}
                        options={weekOfMonthOptions}
                        isDisabled={startVerification === false || disableField('verification_field_pay_frequency_dropdown_option_id')}
                        isSearchable={false}
                        onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            {/* display this option if weekly choosen */}
            {/* display this option if semi-monthly and week day adn week month choosen */}
            {/* display this option if Monthly and Week Day and Week of Month choosen */}
            <div
              className={`columns mb-0 lox-verification-cell ${(payFrequencyValue == 'Weekly' || (payFrequencyValue == 'Semi-Monthly' && payFrequencyValueOption == 'semimonthly-option2') || (payFrequencyValue == 'Monthly' && payFrequencyValueOption == 'monthly-option2')) ? '' : 'is-hidden'}`}
            >
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  {payFrequencyValue == 'Weekly' && (<>Pay day of week:</>)}
                  {payFrequencyValue == 'Semi-Monthly' && payFrequencyValueOption == 'semimonthly-option2' && (
                    <>Day 1:</>
                  )}
                  {payFrequencyValue == 'Monthly' && payFrequencyValueOption == 'monthly-option2' && (<>Day:</>)}
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`
                        ${payFrequencyValue == 'Weekly' ? 'What day of the week do you get paid on?' : ''}
                        ${(payFrequencyValue == 'Semi-Monthly' && payFrequencyValueOption == 'semimonthly-option2') ? 'What days of each week do you get paid?' : ''}
                        ${(payFrequencyValue == 'Monthly' && payFrequencyValueOption == 'monthly-option2') ? 'What week of month and day of that week do you get paid?' : ''}
                  `}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <div style={{ width: '100%' }}>
                  <Controller
                    name="verification_field_pay_first_day_of_week_dropdown_option_id"
                    control={forms.verification_field_pay_frequency_dropdown_option_id.control}
                    defaultValue={''}
                    render={({ field }) => (
                      <Select
                        {...field}
                        name="verification_field_pay_first_day_of_week_dropdown_option_id"
                        {...forms.verification_field_pay_frequency_dropdown_option_id.register('verification_field_pay_first_day_of_week_dropdown_option_id')}
                        classNames={reactSelectClassNames()}
                        options={dayOfWeekOptions}
                        isDisabled={startVerification === false || disableField('verification_field_pay_frequency_dropdown_option_id')}
                        isSearchable={false}
                        onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            {/* display this option if Semi-Monthly and Week Day and Week of Month choosen */}
            <div
              className={`columns mb-0 lox-verification-cell ${(payFrequencyValue == 'Semi-Monthly' && payFrequencyValueOption == 'semimonthly-option2') ? '' : 'is-hidden'}`}
            >
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Week 2:
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`What days of each week do you get paid?`}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <div style={{ width: '100%' }}>
                  <Controller
                    name="verification_field_pay_second_week_of_month_dropdown_option_id"
                    control={forms.verification_field_pay_frequency_dropdown_option_id.control}
                    defaultValue={verificationFields['verification_field_pay_second_week_of_month_dropdown_option_id'] ? {
                      label: verificationFields['verification_field_pay_second_week_of_month_dropdown_option_id'].field_value,
                      value: verificationFields['verification_field_pay_second_week_of_month_dropdown_option_id'].field_value,
                    } : ''}
                    render={({ field }) => (
                      <Select
                        {...field}
                        name="verification_field_pay_second_week_of_month_dropdown_option_id"
                        {...forms.verification_field_pay_frequency_dropdown_option_id.register('verification_field_pay_second_week_of_month_dropdown_option_id')}
                        classNames={reactSelectClassNames()}
                        options={weekOfMonthOptions}
                        isDisabled={startVerification === false || disableField('verification_field_pay_frequency_dropdown_option_id')}
                        isSearchable={false}
                        onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            {/* display this option if Semi-Monthly and Week Day and Week of Month choosen */}
            <div
              className={`columns mb-0 lox-verification-cell ${(payFrequencyValue == 'Semi-Monthly' && payFrequencyValueOption == 'semimonthly-option2') ? '' : 'is-hidden'}`}
            >
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Day 2:
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`What days of each week do you get paid?`}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <div style={{ width: '100%' }}>
                  <Controller
                    name="verification_field_pay_second_day_of_week_dropdown_option_id"
                    control={forms.verification_field_pay_frequency_dropdown_option_id.control}
                    defaultValue={verificationFields['verification_field_pay_second_day_of_week_dropdown_option_id'] ? {
                      label: verificationFields['verification_field_pay_second_day_of_week_dropdown_option_id'].field_value,
                      value: verificationFields['verification_field_pay_second_day_of_week_dropdown_option_id'].field_value,
                    } : ''}
                    render={({ field }) => (
                      <Select
                        {...field}
                        name="verification_field_pay_second_day_of_week_dropdown_option_id"
                        {...forms.verification_field_pay_frequency_dropdown_option_id.register('verification_field_pay_second_day_of_week_dropdown_option_id')}
                        classNames={reactSelectClassNames()}
                        options={dayOfWeekOptions}
                        isDisabled={startVerification === false || disableField('verification_field_pay_frequency_dropdown_option_id')}
                        isSearchable={false}
                        onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            {/* display this option if bi-weekly choosen */}
            <div
              className={`columns mb-0 lox-verification-cell ${payFrequencyValue == 'Bi-Weekly' ? '' : 'is-hidden'}`}
            >
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Next Pay Date:
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`When was your last pay date?`}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <Controller
                  name="verification_field_next_pay_date"
                  control={forms.verification_field_pay_frequency_dropdown_option_id.control}
                  defaultValue={parseDefaultValue('verification_field_next_pay_date')}
                  render={({ field }) => (
                    <input
                      {...field}
                      className="input is-small"
                      type="date"
                      {...forms.verification_field_pay_frequency_dropdown_option_id.register('verification_field_next_pay_date')}
                      disabled={startVerification === false || disableField('verification_field_pay_frequency_dropdown_option_id')}
                      onChange={(val) => field.onChange(val)}
                      min={today}
                      max={maxDate}
                    />
                  )}
                />
              </div>
            </div>

            {/* display this option if Semi-Monthly and Specific Days choosen */}
            {/* display this option if Monthly and Pay Day of Months choosen */}
            <div
              className={`columns mb-0 lox-verification-cell ${((payFrequencyValue == 'Semi-Monthly' && payFrequencyValueOption == 'semimonthly-option1') || (payFrequencyValue == 'Monthly' && payFrequencyValueOption == 'monthly-option1')) ? '' : 'is-hidden'}`}
            >
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  {payFrequencyValue == 'Semi-Monthly' && payFrequencyValueOption == 'semimonthly-option1' && (
                    <>First payment day of month:</>
                  )}
                  {payFrequencyValue == 'Monthly' && payFrequencyValueOption == 'monthly-option1' && (<>Day:</>)}
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`
                    ${(payFrequencyValue == 'Semi-Monthly' && payFrequencyValueOption == 'semimonthly-option1') ? 'What two days do you get paid each month?' : ''}
                    ${(payFrequencyValue == 'Monthly' && payFrequencyValueOption == 'monthly-option1') ? 'What day of the month do you get paid?' : ''}
                  `}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <div style={{ width: '100%' }}>
                  <Controller
                    name="verification_field_pay_first_day_of_month_dropdown_option_id"
                    control={forms.verification_field_pay_frequency_dropdown_option_id.control}
                    defaultValue={''}
                    render={({ field }) => (
                      <Select
                        {...field}
                        name="verification_field_pay_first_day_of_month_dropdown_option_id"
                        {...forms.verification_field_pay_frequency_dropdown_option_id.register('verification_field_pay_first_day_of_month_dropdown_option_id')}
                        classNames={reactSelectClassNames()}
                        options={dayOfMonthOptions}
                        isDisabled={startVerification === false || disableField('verification_field_pay_frequency_dropdown_option_id')}
                        isSearchable={false}
                        onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            {/* display this option if Semi-Monthly and Specific Days choosen */}
            <div
              className={`columns mb-0 lox-verification-cell ${(payFrequencyValue == 'Semi-Monthly' && payFrequencyValueOption == 'semimonthly-option1') ? '' : 'is-hidden'}`}
            >
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Second payment day of month:
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`What two days do you get paid each month?`}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <div style={{ width: '100%' }}>
                  <Controller
                    name="verification_field_pay_second_day_of_month_dropdown_option_id"
                    control={forms.verification_field_pay_frequency_dropdown_option_id.control}
                    defaultValue={''}
                    render={({ field }) => (
                      <Select
                        {...field}
                        name="verification_field_pay_second_day_of_month_dropdown_option_id"
                        {...forms.verification_field_pay_frequency_dropdown_option_id.register('verification_field_pay_second_day_of_month_dropdown_option_id')}
                        classNames={reactSelectClassNames()}
                        options={dayOfMonthOptions}
                        isDisabled={startVerification === false || disableField('verification_field_pay_frequency_dropdown_option_id')}
                        isSearchable={false}
                        onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </form>
          {/* END PAY FREQUENCY */}

          <form onSubmit={forms.verification_field_is_pay_before_holiday.handleSubmit(onSubmit)}>
            <div className="columns mb-0 lox-verification-cell">
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Holiday or Weekend Payday:
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`Now, what happens when your payday falls on a holiday or weekend? Do you get paid the previous business day or the next business day?`}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <div style={{ width: '100%' }}>
                  <Controller
                    name="verification_field_is_pay_before_holiday"
                    control={forms.verification_field_is_pay_before_holiday.control}
                    rules={{ required: true }}
                    defaultValue={verificationFields['verification_field_is_pay_before_holiday'] ?
                      [{ label: 'Before', value: 1 }, { label: 'After', value: 0 }].find(
                        (option) => option.value == verificationFields['verification_field_is_pay_before_holiday'].field_value
                      ) ?? undefined : undefined}
                    render={({ field }) => (
                      <Select
                        {...field}
                        name="verification_field_is_pay_before_holiday"
                        {...forms.verification_field_is_pay_before_holiday.register('verification_field_is_pay_before_holiday')}
                        classNames={reactSelectClassNames()}
                        options={[{ label: 'Before', value: 1 }, { label: 'After', value: 0 }]}
                        isDisabled={startVerification === false || disableField('verification_field_is_pay_before_holiday')}
                        isSearchable={false}
                        onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                      />
                    )}
                  />
                </div>
              </div>
              {renderStatusActions('verification_field_is_pay_before_holiday')}
            </div>
          </form>

          <form onSubmit={forms.verification_field_is_confirm_pay_dates.handleSubmit(onSubmit)}>
            <div className="columns mb-0 lox-verification-cell">
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Confirm Pay Dates:
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`${nextPaymentDatesTooltip}`}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <div style={{ width: '100%' }}>
                  <Controller
                    name="verification_field_is_confirm_pay_dates"
                    control={forms.verification_field_is_confirm_pay_dates.control}
                    rules={{ required: true }}
                    defaultValue={verificationFields['verification_field_is_confirm_pay_dates'] ?
                      [{ label: 'Yes', value: 1 }, { label: 'No', value: 0 }].find(
                        (option) => option.value == verificationFields['verification_field_is_confirm_pay_dates'].field_value
                      ) ?? undefined : undefined}
                    render={({ field }) => (
                      <Select
                        {...field}
                        name="verification_field_is_confirm_pay_dates"
                        {...forms.verification_field_is_confirm_pay_dates.register('verification_field_is_confirm_pay_dates')}
                        classNames={reactSelectClassNames()}
                        options={[{ label: 'Yes', value: 1 }, { label: 'No', value: 0 }]}
                        isDisabled={startVerification === false || disableField('verification_field_is_confirm_pay_dates')}
                        isSearchable={false}
                        onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                      />
                    )}
                  />
                </div>
              </div>
              {renderStatusActions('verification_field_is_confirm_pay_dates')}
            </div>
          </form>

          <form onSubmit={forms.verification_field_is_automatic_payments.handleSubmit(onSubmit)}>
            <div className="columns mb-0 lox-verification-cell">
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Automatic Payments:
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`If you decide to proceed with the loan, is the account on file the account we will be using for automatic payments?`}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <div style={{ width: '100%' }}>
                  <Controller
                    name="verification_field_is_automatic_payments"
                    control={forms.verification_field_is_automatic_payments.control}
                    rules={{ required: true }}
                    defaultValue={verificationFields['verification_field_is_automatic_payments'] ?
                      [{ label: 'Yes', value: 1 }, { label: 'No', value: 0 }].find(
                        (option) => option.value == verificationFields['verification_field_is_automatic_payments'].field_value
                      ) ?? undefined : { label: 'No', value: 0 }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        name="verification_field_is_automatic_payments"
                        {...forms.verification_field_is_automatic_payments.register('verification_field_is_automatic_payments')}
                        classNames={reactSelectClassNames()}
                        options={[{ label: 'Yes', value: 1 }, { label: 'No', value: 0 }]}
                        isDisabled={startVerification === false || disableField('verification_field_is_automatic_payments')}
                        isSearchable={false}
                        onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                      />
                    )}
                  />
                </div>
              </div>
              {renderStatusActions('verification_field_is_automatic_payments')}
            </div>
          </form>

          <div className="divider mb-4 my-2" />
          {/* START BANK ACCOUNT */}
          <form
            onSubmit={forms.verification_field_bank_account_number.handleSubmit(onBankAccountVerificationSubmit)}
          >
            <div className="columns mb-0 lox-verification-cell">
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Account Number:
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`Can you verify your bank Routing and Account number please? `}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <Controller
                  name="verification_field_bank_account_number"
                  control={forms.verification_field_bank_account_number.control}
                  defaultValue={parseDefaultValue('verification_field_bank_account_number')}
                  render={({ field }) => (
                    <input
                      {...field}
                      name="verification_field_bank_account_number"
                      {...forms.verification_field_bank_account_number.register('verification_field_bank_account_number')}
                      className="input is-small"
                      type="text"
                      disabled={startVerification === false || disableField('verification_field_bank_account_number')}
                      onChange={(val) => field.onChange(val)}
                    />
                  )}
                />
              </div>
              <ViewSensitivePiiButtons
                customerId={customerData.id}
                bankAccountId={latestVerificationBankAccountId}
                type={'full_bank_account_number'}
                style={{ marginTop: '6px' }}
              />
              {dropdownOptions.find((o) => o?.id === managerApproval?.mgr_approval_type_dropdown_option_id)?.code_key === 'mgr_approval_type_bank_account' && managerApproval?.bank_account_id == managerApproval?.bank_change_new_bank_account_id && (
                <div style={{ marginTop: '8px' }}>
                  <ManagerApprovalIcons
                    type={dropdownOptions.find((o) => o?.id === managerApproval?.mgr_approval_status_dropdown_option_id)?.code_key}
                  />
                </div>
              )}
            </div>
            <div className="columns mb-0 lox-verification-cell">
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Routing Number:
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`Can you verify your bank Routing and Account number please?`}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <Controller
                  name="verification_field_bank_routing_number"
                  control={forms.verification_field_bank_account_number.control}
                  defaultValue={parseDefaultValue('verification_field_bank_routing_number')}
                  render={({ field }) => (
                    <input
                      {...field}
                      name="verification_field_bank_routing_number"
                      value={field.value ?? ''}
                      className="input is-small"
                      type="text"
                      disabled={startVerification === false || disableField('verification_field_bank_account_number')}
                      onChange={(e) => {
                        const value = e.target.value;
                        field.onChange(value);

                        // Use debounced lookup for better performance
                        debouncedLookupBankName(value);
                      }}
                    />
                  )}
                />
              </div>
            </div>
            <div className="columns mb-0 lox-verification-cell">
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Bank Name:
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`I have your account with ${currentBank?.label ?? '___'} is that correct?`}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <div style={{ width: '100%' }} className="is-flex is-align-items-center">
                  <div style={{ flex: 1 }}>
                    <Controller
                      name="verification_field_bank_name"
                      control={forms.verification_field_bank_account_number.control}
                      rules={{ required: true }}
                      defaultValue={''}
                      render={({ field }) => (
                        <AsyncSelect
                          {...field}
                          name="verification_field_bank_name"
                          {...forms.verification_field_bank_account_number.register('verification_field_bank_name')}
                          classNames={reactSelectClassNames()}
                          isDisabled={startVerification === false || disableField('verification_field_bank_account_number')}
                          isSearchable
                          onChange={(val) => field.onChange({ label: val?.label, value: val?.id })}
                          cacheOptions
                          defaultOptions={false}
                          loadOptions={(inputValue) => getBanks(inputValue, 'label', 'like')}
                          getOptionLabel={(e) => parseLabel(e)}
                          getOptionValue={(e) => parseValue(e)}
                          noOptionsMessage={({ inputValue }) => (!inputValue ? 'Start typing to search...' : 'No results found')}
                          styles={{
                            container: (base) => ({ ...base, width: '100%' }),
                            menu: ({ ...css }) => ({ ...css, width: 'max-content' }),
                          }}
                        />
                      )}
                    />
                  </div>
                  {/* Loading indicator for bank lookup - positioned to the right */}
                  {bankLookupLoading && (
                    <div className="has-text-info is-size-7 ml-2">
                      <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="columns mb-0 lox-verification-cell">
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Bank Account Type:
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`Is this a checking or savings account?`}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <div style={{ width: '100%' }}>
                  <Controller
                    name="verification_field_is_bank_savings"
                    control={forms.verification_field_bank_account_number.control}
                    rules={{ required: true }}
                    defaultValue={verificationFields['verification_field_is_bank_savings'] ?
                      [{ label: 'Savings', value: 1 }, { label: 'Checking', value: 0 }].find(
                        (option) => option.value == verificationFields['verification_field_is_bank_savings'].field_value
                      ) ?? undefined : undefined}
                    render={({ field }) => (
                      <Select
                        {...field}
                        name="verification_field_is_bank_savings"
                        {...forms.verification_field_bank_account_number.register('verification_field_is_bank_savings')}
                        classNames={reactSelectClassNames()}
                        options={[{ label: 'Savings', value: 1 }, { label: 'Checking', value: 0 }]}
                        isDisabled={startVerification === false || disableField('verification_field_bank_account_number')}
                        isSearchable={false}
                        onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="columns mb-0 lox-verification-cell">
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Online Banking:
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`Do you have online banking set up?`}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <div style={{ width: '100%' }}>
                  <Controller
                    name="verification_field_is_online_banking"
                    control={forms.verification_field_bank_account_number.control}
                    rules={{ required: true }}
                    defaultValue={verificationFields['verification_field_is_online_banking'] ?
                      [{ label: 'Yes', value: 1 }, { label: 'No', value: 0 }].find(
                        (option) => option.value == verificationFields['verification_field_is_online_banking'].field_value
                      ) ?? undefined : undefined}
                    render={({ field }) => (
                      <Select
                        {...field}
                        name="verification_field_is_online_banking"
                        {...forms.verification_field_bank_account_number.register('verification_field_is_online_banking')}
                        classNames={reactSelectClassNames()}
                        options={[{ label: 'Yes', value: 1 }, { label: 'No', value: 0 }]}
                        isDisabled={startVerification === false || disableField('verification_field_bank_account_number')}
                        isSearchable={false}
                        onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="columns mb-0 lox-verification-cell">
              <div className="column is-one-third">
                <label className="label has-text-weight-normal verification-label">
                  Direct Deposit:
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                    data-tooltip={`Do you receive direct deposit from that source of income into the bank account we have on file?`}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </label>
              </div>
              <div className="column is-flex is-align-items-center is-one-quarter">
                <div style={{ width: '100%' }}>
                  <Controller
                    name="verification_field_is_direct_deposit"
                    control={forms.verification_field_bank_account_number.control}
                    rules={{ required: true }}
                    defaultValue={verificationFields['verification_field_is_direct_deposit'] ?
                      [{ label: 'Yes', value: 1 }, { label: 'No', value: 0 }].find(
                        (option) => option.value == verificationFields['verification_field_is_direct_deposit'].field_value
                      ) ?? undefined : undefined}
                    render={({ field }) => (
                      <Select
                        {...field}
                        name="verification_field_is_direct_deposit"
                        {...forms.verification_field_bank_account_number.register('verification_field_is_direct_deposit')}
                        classNames={reactSelectClassNames()}
                        options={[{ label: 'Yes', value: 1 }, { label: 'No', value: 0 }]}
                        isDisabled={startVerification === false || disableField('verification_field_bank_account_number')}
                        isSearchable={false}
                        onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                      />
                    )}
                  />
                </div>
              </div>
              {renderStatusActions('verification_field_bank_account_number')}
            </div>
          </form>
          <br />
          {/* END BANK ACCOUNT */}
          {isReadOnly ? (
            <div className="columns mb-0 lox-verification-cell">
              <div className="column is-flex is-1 is-offset-11">
                <div className="column is-flex is-align-items-center p-0 m-0">
                  <button
                    className="button is-link is-small is-fullwidth"
                    disabled={!isVerificationCompleted(verificationFields, processingStatusCompletedId) || saving || disableNextButton}
                    onClick={() => processVerificationQuestions()}
                  >
                    {saving ? 'Validating...' : 'Next'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="columns mb-0 lox-verification-cell">
              <div className="column is-flex is-1 is-offset-11">
                <div className="column is-flex is-align-items-center p-0 m-0">
                  <button
                    className="button is-link is-small is-fullwidth"
                    disabled={!isVerificationCompleted(verificationFields, processingStatusCompletedId) || saving || disableNextButton}
                    onClick={() => processVerificationQuestions()}
                  >
                    {saving ? 'Validating...' : 'Next'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="column is-one-quarters">
          <div className="message is-white lox-dashlet-detail">
            <div className="message-header has-background-grey has-text-white lox-dashlet-detail__title py-0">
              <p className="is-size-4">Pre-Approval Loan Terms</p>
            </div>
            <div className="message-body py-0 px-1">
              <div className="columns m-0">
                <p className="column is-two-third py-1">Max Principal</p>
                <p className="column is-one-third py-1"><CurrencyFormat value={currentPtiTerms?.max_principal} /></p>
              </div>

              <div className="columns m-0">
                <p className="column is-two-third py-1">Min Principal</p>
                <p className="column is-one-third py-1"><CurrencyFormat value={currentPtiTerms?.min_principal} /></p>
              </div>

              <div className="columns m-0">
                <p className="column is-two-third py-1">Max APR</p>
                <p className="column is-one-third py-1"><PercentFormat value={currentPtiTerms?.max_apr} fractionDigits={3} /></p>
              </div>

              <div className="columns m-0">
                <p className="column is-two-third py-1">Min APR</p>
                <p className="column is-one-third py-1"><PercentFormat value={currentPtiTerms?.min_apr} fractionDigits={3} /></p>
              </div>

              <div className="columns m-0">
                <p className="column is-two-third py-1">Max Payment Amount</p>
                <p className="column is-one-third py-1"><CurrencyFormat value={currentPtiTerms?.max_payment_amount} />
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

/**
 * Parses API error responses and builds a formatted list of error messages
 */
function parseErrorResponse(errors, fieldsData = {}) {
  // Build list of error messages
  const errorMessages = [];

  // Process each field error
  Object.entries(errors).forEach(([field, messages]) => {
    // Get display name for the field if provided in fieldsData, otherwise use the field name
    const fieldDisplayName = fieldsData[field] || formatFieldName(field);

    // Handle case where messages is an array
    if (typeof messages === 'string') {
      errorMessages.push(
        <li key={`${field}-string`}>
          {fieldDisplayName}: {messages || 'field is required'}
        </li>
      );
    } else if (typeof messages === 'object') {
      Object.entries(messages).forEach(([key, message]) => {
        if (typeof message === 'string') {
          errorMessages.push(
            <li key={`${field}-${key}`}>
              {fieldDisplayName}: {message || 'field is required'}
            </li>
          );
        }
      });
    }
  });

  return <ul style={{ listStyle: "disc", marginLeft: "25px" }}>{errorMessages}</ul>;
}

/**
 * Formats a field name into a human-readable format
 */
function formatFieldName(fieldName) {
  // Remove common prefixes like 'verification_field_'
  let formattedName = fieldName.replace(/^verification_field_/, '');
  // Replace underscores with spaces
  formattedName = formattedName.replace(/_/g, ' ');
  // Capitalize first letter of each word
  formattedName = formattedName
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  return formattedName;
}
